// includes/js-src/log/log-render.ts
//
// Client-side renderer for combat-log rows. Driven by handleRollInsert
// (template.js) for WebSocket push, and by renderLogWindowFromJson
// (containers.ts) for initial-load buildLog() refreshes. Both call
// window.renderSimpleLogRow(roll).
//
// All rendering is pure-static: thumbnails read pic_url straight from the
// JSON snapshot taken at insert time. NO DB hunts, NO fetch() calls.

import { parseText } from '../shared/parse-text';
import { statusRunes, statusRuneMountHtml } from '../shared/status-rune';
import { interpretRollResultArr, parseRollsFull } from './interpret';
import { renderDeathSessionBody } from './containers';
import type {
    Roll,
    RollHandler,
    RollHandlerOpts,
    RollHandlerResult,
    GroupContainerDef,
    CollapseLabelConfig,
    RollInsertMessage,
} from './types';

function _logCall(name: string, ...args: unknown[]): unknown {
    const fn = (window as unknown as Record<string, unknown>)[name];
    return typeof fn === 'function' ? fn(...args) : undefined;
}

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-log-action]')
        : null;
    if (!el) return;
    event.preventDefault();
    const action = el.dataset.ftLogAction || '';
    if (action === 'open-item-info') _logCall('openItemInfo', el.dataset.ftItemId || '');
    if (action === 'make-roll-public') _logCall('makeRollPublic', el.dataset.ftRollId || '');
    // GM prompt rows (stealth-spotted, out-of-combat hostile action): the GM chose to roll
    // initiative seeded from the named creature. Both rows share this one-shot button.
    if (action === 'stealth-roll-initiative' || action === 'roll-initiative-from-seed') {
        // One shot: the prompt row stays in history, but the button only fires once per render.
        if ((el as HTMLButtonElement).disabled) return;
        (el as HTMLButtonElement).disabled = true;
        el.textContent = 'Rolling initiative…';
        _logCall('rollInitiativeFromSeed', el.dataset.ftCreatureId || '');
    }
});

// ===================================================================
// Helpers
// ===================================================================

function normalizePicUrl(v: unknown): string | null {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'string') return v;
    if (Array.isArray(v)) {
        const first = v[0];
        if (typeof first === 'string' && first !== '') return first;
        return null;
    }
    return null;
}

function _esc(s: unknown): string {
    if (typeof escapeHtml === 'function') return escapeHtml(String(s ?? ''));
    const map: Record<string, string> = {
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    };
    return String(s ?? '').replace(/[&<>"']/g, m => map[m] ?? m);
}

function _isUuid(s: unknown): boolean {
    if (typeof checkIfValidUUID === 'function') return checkIfValidUUID(String(s));
    return typeof s === 'string' && /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/.test(s);
}

function _ucwords(s: unknown): string {
    if (typeof ucwordsAndSpace === 'function') return ucwordsAndSpace(String(s ?? ''));
    return String(s || '').replace(/_/g, ' ').split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function _icon(key: unknown, w?: number, h?: number): string {
    if (typeof iconMap !== 'function') return '';
    const k = String(key ?? '');
    const html = w == null && h == null
        ? iconMap(k)
        : iconMap(k, w || 25, h || w || 25);
    if (!html) return '';
    const registry = (window as unknown as { FT_ICON_REGISTRY?: Record<string, unknown> }).FT_ICON_REGISTRY
        || (typeof ICON_LOOKUP !== 'undefined' ? ICON_LOOKUP : {});
    const singular = k.endsWith('s') ? k.replace(/s+$/, '') : '';
    const hasRegisteredIcon = Object.prototype.hasOwnProperty.call(registry, k)
        || (singular !== '' && Object.prototype.hasOwnProperty.call(registry, singular));
    // The compact FT3D log projects this already-authorized markup instead of interpreting the
    // Roll payload again. Preserve the renderer's semantic icon key so that projection can reuse
    // exactly ONE of the icons this row already chose; otherwise flattening the <img> to text drops
    // almost the entire icon vocabulary and leaves only the small phrase fallback table. Do not
    // carry iconMap's help-circle fallback: a mystery "?" adds noise rather than information.
    return hasRegisteredIcon ? `<span data-ft-log-icon="${_esc(k)}">${html}</span>` : html;
}

function _infoAttrs(id: unknown): string {
    return `role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(id)}"`;
}

function _infoSpan(id: unknown, html: string, className = ''): string {
    const cls = className ? ` class="${className}"` : '';
    return `<span${cls} ${_infoAttrs(id)}>${html}</span>`;
}

// ===================================================================
// renderLogLineThumbnail
// ===================================================================

type ItemArr = Record<string, unknown>;
interface ThumbnailOpts {
    width?: number;
    height?: number;
    thumbnail_link?: number;
    img_classes?: string;
}

/** Read [0] from a possibly-array field on a JSON snapshot. Items-table columns
 *  (item_id/item_name/item_type) are bare scalars per the table-of-origin shape;
 *  layer/related-table buckets (creature_number, pic_url, etc.) are [0]-wrapped.
 *  Pass either shape through to a single scalar so callers can String()/Number()
 *  it directly. */
function _first(v: unknown): unknown {
    if (Array.isArray(v)) return v[0];
    if (v === null || v === undefined) return undefined;
    if (typeof v === 'object') return undefined;
    return v;
}

function renderLogLineThumbnail(item_arr: object | null | undefined, opts?: ThumbnailOpts): string {
    const arr: ItemArr = (item_arr || {}) as ItemArr;
    opts = opts || {};
    const width = opts.width != null ? opts.width : 60;
    const height = opts.height != null ? opts.height : 60;
    const link = opts.thumbnail_link != null ? opts.thumbnail_link : 1;
    const imgClass = opts.img_classes || 'img-thumbnail';

    const picUrl = normalizePicUrl(arr.pic_url);
    const itemId = String(_first(arr.item_id) || '');
    const itemName = String(_first(arr.item_name) || '');
    const itemType = String(_first(arr.item_type) || '').toLowerCase();

    let inner;
    if (picUrl !== null) {
        const containerStyle = `width:${width}px;height:${height}px;display:flex;align-items:center;justify-content:center;overflow:hidden;`;
        const imgStyle = 'max-width:100%;max-height:100%;width:auto;height:auto;';
        inner = `<div style="${containerStyle}"><img src="${_esc(picUrl)}" class="${_esc(imgClass)}" style="${imgStyle}" alt="${_esc(itemName)}"></div>`;
    } else if (itemType === 'lair') {
        // Lairs have no portrait — show the Lair icon (parity with the XZ map token / edit form), never
        // the name-initials avatar a normal creature falls back to. A lair WITH a picture takes the pic
        // branch above.
        const boxStyle = `height:${width}px;width:${width}px;`;
        // iconMap(...false) suppresses the 'lair' icon's built-in "Lair" tooltip — the wrapper below
        // already carries the lair's encounter-creature name as the tooltip, which the owner prefers.
        const lairIcon = (typeof iconMap === 'function') ? iconMap('lair', width, width, false) : _icon('lair', width, width);
        const iconBox = `<div style="${boxStyle}" class="d-flex justify-content-center align-items-center overflow-hidden">${lairIcon}</div>`;
        inner = (link === 1 && itemId)
            ? `<div class="cursor-pointer" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${iconBox}</div>`
            : iconBox;
    } else {
        const initials = itemName ? (itemName.charAt(0).toUpperCase() + (itemName.length > 1 ? itemName.charAt(1) : '')) : '';
        const boxStyle = `height:${width}px;width:${width}px;`;
        if (link === 1 && itemId) {
            inner = `<div style="${boxStyle}" class="bg-warning border border-dark rounded d-flex justify-content-center align-items-center cursor-pointer" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}"><span class="fw-bold h4 text-dark">${_esc(initials)}</span></div>`;
        } else {
            inner = `<div style="${boxStyle}" class="bg-warning border border-dark rounded d-flex justify-content-center align-items-center"><span class="fw-bold h4 text-dark">${_esc(initials)}</span></div>`;
        }
    }

    const creatureNumber = Number(_first(arr.creature_number) || 0);
    if (creatureNumber > 0) {
        inner += `<div class="position-absolute bottom-0 start-0"><span class="badge bg-dark text-light border">${_esc(creatureNumber)}</span></div>`;
    }

    // Lairs are ALWAYS private (that's how they can't be targeted), so the private badge is just noise on a
    // lair log token — and a lair's actions ARE shown to players. Suppress it for lairs; keep it for every
    // other private creature.
    if (_first(arr.encounter_creature_private) && itemType !== 'lair') {
        inner += `<div class="position-absolute bottom-0 end-0 extra_icons_info">${_icon('encounter_creature_private')}</div>`;
    }

    const tooltip = _esc(itemName);
    if (link === 1 && itemId) {
        return `<div class="position-relative cursor-pointer" role="button" data-bs-toggle="tooltip" title="${tooltip}" data-bs-original-title="${tooltip}" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${inner}</div>`;
    }
    return `<div class="position-relative" data-bs-toggle="tooltip" title="${tooltip}" data-bs-original-title="${tooltip}">${inner}</div>`;
}

// ===================================================================
// renderLogTimestamp
// ===================================================================

function renderLogTimestamp(roll: Roll, hasCampaignId: boolean, opts?: { noBorder?: boolean }): string {
    opts = opts || {};
    const isPrivate = Number(roll.roll_private || 0);
    const campaignPermission = !!(window.FateTide && window.FateTide.user && window.FateTide.user.isGM);
    const rollId = String(roll.roll_id || '');

    let leftIcons = '';
    if (isPrivate === 1) {
        const makePublic = campaignPermission ? ` role="button" data-ft-log-action="make-roll-public" data-ft-roll-id="${_esc(rollId)}"` : '';
        leftIcons += `<svg class="svg-icon" style="font-size:10px;" aria-hidden="true"${makePublic}><use href="#eye-slash"></use></svg> `;
    } else if (isPrivate === 2) {
        const makePublic = campaignPermission ? ` role="button" data-ft-log-action="make-roll-public" data-ft-roll-id="${_esc(rollId)}"` : '';
        leftIcons += `<svg class="svg-icon" style="font-size:10px;" aria-hidden="true"${makePublic}><use href="#user-secret"></use></svg> `;
    }

    let calendarText = '';
    if (hasCampaignId) {
        const h = Number(roll.roll_time_hour || 0);
        const m = Number(roll.roll_time_minute || 0);
        const s = Number(roll.roll_time_second || 0);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h === 0 ? 12 : (h >= 13 ? h - 12 : h);
        const mm = m < 10 ? '0' + m : String(m);
        const ss = s < 10 ? '0' + s : String(s);
        const date = `${roll.roll_time_year || 0}-${roll.roll_time_month || 0}-${roll.roll_time_day || 0}`;
        calendarText = `${date} | ${h12}:${mm}:${ss} ${ampm}`;
    }

    let utcLocal = '';
    if (roll.roll_t) {
        try {
            const d = new Date(String(roll.roll_t).replace(' ', 'T') + 'Z');
            if (!isNaN(d.getTime())) {
                const pad = (n: number) => (n < 10 ? '0' + n : String(n));
                const hr = d.getHours();
                const hr12 = hr === 0 ? 12 : (hr > 12 ? hr - 12 : hr);
                const ap = hr >= 12 ? 'PM' : 'AM';
                utcLocal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(hr12)}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${ap}`;
            }
        } catch (_) { /* ignore parse failure */ }
    }

    const footerCls = opts.noBorder
        ? 'row mb-0 px-2 border-top mx-0'
        : 'row mb-2 px-2 border-bottom border-start border-end rounded-bottom';
    return `<div class="${footerCls}">
        <div class="col-6 text-left">${leftIcons}<span class="fw-bold" style="font-size: 10px;">${_esc(calendarText)}</span></div>
        <div class="col-6 text-right"><span class="fw-bold" style="font-size: 10px;">${_esc(utcLocal)}</span></div>
    </div>`;
}

// ===================================================================
// _wrapRow
// ===================================================================

function _wrapRow(roll: Roll, bodyHtml: string, opts?: RollHandlerOpts): string {
    opts = opts || {};
    const rollId = _esc(roll.roll_id || '');
    const hasCampaignId = !!roll.campaign_id;
    const showThumbnail = opts.suppressByThumbnail ? false : true;
    const colByThumb = showThumbnail
        ? `<div class="d-flex justify-content-center align-items-center col-3 border-end p-1">${renderLogLineThumbnail(roll.roll_by_arr || {}, { thumbnail_link: 1 })}</div>`
        : '';
    // Hidden roll (roll_private=1) + non-GM viewer: legacy log_display_inc.php set
    // $single_roll_row_format=1, which suppressed the big-result column entirely so
    // the raw roll_result couldn't leak alongside the hidden_roll icon the dice
    // breakdown was already redacted to. Mirror that here.
    const _viewerHasCampaignPerm = Number(roll.campaign_permission || (window.FateTide?.user?.isGM ? 1 : 0)) === 1;
    const _privateForViewer = Number(roll.roll_private || 0) > 0 && !_viewerHasCampaignPerm;
    const showBigResult = !!opts.showBigResult && !_privateForViewer;
    const bodyColClass = !showThumbnail
        ? 'col-12'
        : (showBigResult ? 'col-7' : 'col-9');
    const bigResultText = opts.bigResultText != null
        ? String(opts.bigResultText)
        : String(roll.roll_result ?? '');
    const bigResultFontRem = bigResultText.length <= 2 ? 2
        : bigResultText.length === 3 ? 1.4
        : bigResultText.length === 4 ? 1.05
        : 0.85;
    const bigResultBlock = showBigResult
        ? `<div class="d-flex col-2 border-start justify-content-center align-items-center text-center"><div><h2 class="mb-0" style="font-size:${bigResultFontRem}rem;line-height:1.1;">${_esc(bigResultText)}</h2></div></div>`
        : '';
    const banner = renderHouseRuleBanner(roll);
    if (opts.inline) {
        const cardCls = opts.borderless ? 'row p-1' : 'row p-1 border rounded';
        return `<div class="${cardCls}" style="min-height:70px;">
            ${colByThumb}
            <div class="${bodyColClass} d-flex align-items-center">
                <div class="col-12 p-2">${banner}${bodyHtml}</div>
            </div>
            ${bigResultBlock}
        </div>`;
    }
    const timestampHtml = opts.suppressTimestamp ? '' : renderLogTimestamp(roll, hasCampaignId);
    const rollIdAttr = opts.suppressDataRollId ? '' : `data-ft-roll-id="${rollId}"`;
    return `<li class="list-group-item mb-1 pb-0 pt-0 border-bottom-0" ${rollIdAttr} style="position: relative;">
        <div class="row p-1 border rounded" style="min-height:70px;">
            ${colByThumb}
            <div class="${bodyColClass} d-flex align-items-center">
                <div class="col-12 p-2">${banner}${bodyHtml}</div>
            </div>
            ${bigResultBlock}
        </div>
        ${timestampHtml}
    </li>`;
}

function _roleToBlock(roll: Roll) {
    if (!roll.roll_to) return '';
    return `<div class="d-flex align-items-center justify-content-center col-12 pt-2" data-ft-log-target="1">${renderLogLineThumbnail(roll.roll_to_arr || {}, { thumbnail_link: 1 })}</div>`;
}

// ===================================================================
// Family renderers
// ===================================================================

const SIMPLE_MESSAGE_TYPES = new Set([
    'the_sun_begins_to_rise', 'the_sun_has_risen', 'the_sun_begins_to_set', 'the_sun_has_set',
    'hunger_thirst_enabled', 'hunger_thirst_disabled',
    'waking_weariness_enabled', 'waking_weariness_disabled',
    'haven_return_enabled', 'haven_return_disabled',
    'campaign_downtime_enabled', 'campaign_downtime_disabled',
    'session_resumed', 'session_paused', 'theatre_of_the_mind',
    'player_sheet_haven_return_blocked', 'player_sheet_haven_return_allowed',
    'player_sheet_haven_return_enabled', 'player_sheet_haven_return_disabled',
    '10_second_warning', 'turn_timer_paused', 'turn_timer_resumed', 'turn_timer_reset', 'gm_added_turn_time',
    'previous_turn', 'initiative_order_changed',
    'end_turn', 'combat_ended',
    'concentration_broken', 'concentration_maintained', 'end_concentration',
    'target_retries_contesting_roll_on_end_of_turn', 'armor_bending_roll_failed', 'spell_miscast',
    'counterspell_armed', 'counterspell_fizzled', 'counterspell_success', 'counterspell_failed', 'counterspell_tie',
    'counterspell_circle_locked', 'counterspell_sphere_locked', 'counterspell_spell_locked',
    'creature_hidden', 'creature_revealed', 'creature_private', 'creature_public',
    'all_creatures_hidden', 'all_creatures_revealed', 'all_creatures_private', 'all_creatures_public',
    'set_campaign_campsite', 'set_campaign_stronghold',
    // FT3D party vote: the ONLY durable trace of an otherwise wholly ephemeral table poll.
    'the_party_votes_yes', 'the_party_votes_no', 'the_party_is_divided', 'the_party_abstains',
    'target_creature_no_cover', 'target_creature_revealed', 'target_inventory_revealed',
    'spot_hidden_creatures_search', 'search_for_hidden_inventory_search',
    'ready', 'help', 'disengage', 'withdraw', 'dodging',
    'add_ap', 'add_reaction', 'reaction_gained', 'merchant_haggle', 'add_inventory',
    'long_rest', 'short_rest', 'party_long_rested',
    'summoned_creature', 'summon_dismissed', 'summon_recalled',
    'shapeshifted', 'reverted_shapeshift', 'lost_shapeshift',
    'lockpicking', 'lock_picked', 'failed_to_pick_lock', 'cancelled_lockpicking',
    'casting', 'casting_ritual',
    'swapped_hands', 'forced_move',
    'all_dust_is_lost', 'well_of_creation_connected', 'partial_disenchant',
    'burning_extinguished', 'lost_a_mirror_image',
    'status_stack_increased', 'status_stack_decreased', 'status_stack_decayed',
    'go_to_creature_turn',
    'encounter_source_light_changed', 'encounter_source_light_removed',
    'gm_roll_reveal',
    'unlucky_roll',
    'added_creatures',
    'damage_heals_caster', 'healing_damages_caster',
    'self_adjust_attack',
    'random_creature', 'marching_order_updated',
    'no_charges_to_maintain',
]);

function renderSimpleMessage(roll: Roll) {
    const t = roll.roll_type_1;
    const explanation = t === 'counterspell_armed' ? (parseRollsFull(roll.rolls_full) ?? [])
        .filter(face => Array.isArray(face) && typeof face[1] === 'string').map(face => face[1]).join(' ') : '';
    const reason = explanation ? `<div class="text-muted">${_esc(explanation)}</div>` : '';
    return `<div class="text-left col-12">${_icon(t)} ${_esc(_ucwords(t))}${reason}</div>`;
}

// Prepare / Reset prepared-spell-list: roll_result carries the chosen list number (1/2/3) and
// roll_item_id carries the spellcasting class (item_id_used_arr → roll_item_id_item_name) for the
// multiclass context. Show the list number in the big-result column and the class on the header
// line, so the row reads "Prepare Spell List — Wizard | [2]" instead of a bare label.
function renderPrepareSpellList(roll: Roll) {
    const t        = roll.roll_type_1;
    const listNum  = String(roll.roll_result ?? '').trim();
    const classId   = String(roll.roll_item_id || '');
    const className = String(roll.roll_item_id_item_name || '').trim();
    // The class glyph (iconMap of its UUID) sits just before the name — same icon+name pairing the other
    // item-bearing log rows use (e.g. renderGmChangedEncounterLightType).
    const classIcon = (className && _isUuid(classId)) ? `<span class="mx-1">${_icon(classId, 16, 16)}</span>` : ' ';
    const classHtml = className
        ? (_isUuid(classId)
            ? ` &mdash;${classIcon}${_infoSpan(classId, _esc(className), 'link-primary')}`
            : ` &mdash; ${_esc(className)}`)
        : '';
    return {
        showBigResult: listNum !== '',
        bigResultText: listNum,
        body: `<div class="text-left col-12"><span class="me-2">${_icon(t)}</span>${_esc(_ucwords(t))}${classHtml}</div>`,
    };
}

function renderGmChangedEncounterLightType(roll: Roll) {
    const t        = roll.roll_type_1;
    const itemId   = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    const lightIcon = itemId ? _icon(itemId) : _icon('light_type');
    const lightName = itemId
        ? _infoSpan(itemId, _esc(itemName), 'link-primary ms-2')
        : `<span class="ms-2">${_esc(itemName || 'Automatic')}</span>`;
    return `<div class="text-left">
        <div class="border-bottom pb-1 mb-1 col-12">
            ${_icon(t)} ${_esc(_ucwords(t))}
        </div>
        <div class="d-flex align-items-center justify-content-center col-12">
            ${lightIcon}
            ${lightName}
        </div>
    </div>`;
}

function renderGmChangedEncounterWeather(roll: Roll) {
    const t        = roll.roll_type_1;
    const itemId   = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    const weatherIcon = itemId ? _icon(itemId) : _icon('weather');
    return `<div class="text-left">
        <div class="border-bottom pb-1 mb-1 col-12">
            ${_icon(t)} ${_esc(_ucwords(t))}
        </div>
        <div class="d-flex align-items-center justify-content-center col-12">
            ${weatherIcon}
            ${_infoSpan(itemId, _esc(itemName), 'link-primary ms-2')}
        </div>
    </div>`;
}

// End Concentration: header line + the spell that was being concentrated on. The backend
// (end_concentration_roll) passes the origin spell as roll_to_arr, so the name rides the
// denormalized roll_to_item_name column and the id lives on roll_to_arr.items.item_id (roll_to
// itself stays null — a spell template is not an EC/inventory/encounter destination). Falls back
// to the plain simple-message render when no origin resolved (legacy rows / origin lookup miss).
function renderEndConcentration(roll: Roll) {
    const t = roll.roll_type_1;
    const to: ItemArr = (roll.roll_to_arr as ItemArr | null | undefined) || {};
    const items: ItemArr = (to.items as ItemArr | null | undefined) || {};
    const spellId   = String(roll.roll_to || _first(items.item_id) || _first(to.item_id) || '');
    const spellName = String(roll.roll_to_item_name || _first(items.item_name) || _first(to.item_name) || '').trim();
    const spellType = String(roll.roll_to_item_type || _first(items.item_type) || '').trim();

    // No origin resolved (legacy row / lookup miss) → plain simple-message render.
    if (!spellName) {
        return `<div class="text-left col-12">${_icon(t)} ${_esc(_ucwords(t))}</div>`;
    }

    // Header + <hr> (border-bottom), then a centered line with the spell's item-type icon and a
    // linked name — same shape as renderGmChangedEncounterLightType / the inventory-action rows.
    const typeIcon = spellType ? _icon(spellType) : _icon(t);
    const nameHtml = (spellId && _isUuid(spellId))
        ? _infoSpan(spellId, _esc(spellName), 'link-primary ms-2')
        : `<span class="ms-2">${_esc(spellName)}</span>`;

    return `<div class="text-left">
        <div class="border-bottom pb-1 mb-1 col-12">
            ${_icon(t)} ${_esc(_ucwords(t))}
        </div>
        <div class="d-flex align-items-center justify-content-center col-12">
            ${typeIcon}${nameHtml}
        </div>
    </div>`;
}

// Port of PHP display_seconds_f: collapses to the largest non-zero unit (d / hr / min / sec).
function _displaySecondsF(seconds: number): string {
    const isNegative = seconds < 0;
    let s = Math.abs(seconds | 0);
    const days = Math.floor(s / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const rem = s % 60;
    let out: string;
    if (days > 0)       out = days    + ' day' + (days > 1 ? 's' : '');
    else if (hours > 0) out = hours   + ' hr'  + (hours > 1 ? 's' : '');
    else if (minutes > 0) out = minutes + ' min' + (minutes > 1 ? 's' : '');
    else                out = rem     + ' sec' + (rem > 1 ? 's' : '');
    return isNegative ? '-' + out : out;
}

const CAMPAIGN_TIME_PUSH_PULL_HEADERS: Record<string, string> = {
    gm_pushed_campaign_time:        'GM Pushed Time',
    gm_pulled_campaign_time:        'GM Pulled Time',
    campaign_downtime_time_pushed:  'Campaign downtime &mdash; time pushed',
};

function renderCampaignTimePushPull(roll: Roll) {
    const t = String(roll.roll_type_1 || '');
    const header = CAMPAIGN_TIME_PUSH_PULL_HEADERS[t] || _esc(_ucwords(t));
    const seconds = parseInt(String(roll.roll_result ?? 0), 10) || 0;
    return `<div class="text-left">
        <div class="border-bottom pb-1 mb-1 col-12">
            ${_icon(t)} ${header}
        </div>
        <div class="d-flex align-items-center justify-content-center col-12">
            ${_esc(_displaySecondsF(seconds))}
        </div>
    </div>`;
}

// Header-plus-numeric family: roll_type_1 carries the meaning, roll_result
// carries a single scalar that belongs in the big-result column. Covers
// signed deltas (self_adjust_*, ap_change_*) and post-clamp counts
// (increased_/decreased_weapon_attack, increased_/decreased_head_attack).
const SCALAR_BIG_RESULT_TYPES = [
    'self_adjust_v', 'heal_to_v_max',
    'self_adjust_temp_v', 'self_adjust_max_v', 'self_adjust_temp_max_v',
    'self_adjust_hunger', 'self_adjust_thirst', 'self_adjust_mana', 'self_adjust_luck',
    'ap_change_during_turn', 'ap_change_to_caster_during_turn',
    'increased_weapon_attack', 'decreased_weapon_attack',
    'increased_head_attack', 'decreased_head_attack',
];

function renderScalarBigResult(roll: Roll) {
    const t = roll.roll_type_1;
    const raw = Number(roll.roll_result ?? 0);
    const n = Number.isFinite(raw) ? raw : 0;
    return {
        showBigResult: true,
        bigResultText: String(n),
        body: `<div class="row m-0"><div class="col-12 text-left">${_icon(t)} ${_esc(_ucwords(t))}</div></div>`,
    };
}

const CREATURE_STATE_TEXT: Record<string, string> = {
    creature_killed:         ' is killed.',
    creature_killed_forever: ' is killed forever!',
    creature_destroyed:      ' is destroyed!',
    creature_resurrected:    ' is resurrected!',
};

function renderCreatureStateIconText(roll: Roll) {
    const t = roll.roll_type_1;
    const verb = CREATURE_STATE_TEXT[t] || (' ' + _ucwords(t));
    return `<div class="row m-0"><div class="col-12 text-left">${_icon(t)}${_esc(verb)}</div></div>`;
}

// Stealth v2 (owner 2026-08-27): a hidden creature was SPOTTED. The server inserts this row
// roll_private=1 (GM-only) beside the public target_creature_revealed row. roll_by = the spotter,
// roll_to = the creature that was spotted. The GM decides whether the sighting starts combat — the
// button seeds initiative from the spotter (roll-init.ts rollInitiativeFromSeed); never automatic.
function renderStealthSpottedGmPrompt(roll: Roll) {
    // Rows carry the spotter as roll_by (roll_creature_id is an alias only some paths set).
    const spotterId = String(roll.roll_by || roll.roll_creature_id || '');
    const spotter = String(roll.roll_by_item_name || roll.roll_creature_name || 'The spotter');
    const spotted = String(roll.roll_to_item_name || roll.roll_target_creature_name || 'a hidden creature');
    // Same viewer-permission rule every other handler uses (the row's campaign_permission, else the GM flag).
    const isGm = Number(roll.campaign_permission || (window.FateTide?.user?.isGM ? 1 : 0)) === 1;
    const button = isGm && spotterId
        ? `<div class="col-12 pt-2 text-center"><button type="button" class="btn btn-sm btn-warning" data-ft-log-action="stealth-roll-initiative" data-ft-creature-id="${_esc(spotterId)}">Roll initiative from ${_esc(spotter)}?</button></div>`
        : '';
    return `<div class="row m-0"><div class="col-12 text-left">${_icon('target_creature_revealed')} <strong>Spotted!</strong> ${_esc(spotter)} noticed ${_esc(spotted)} hiding.</div>${button}</div>`;
}

// Hard delete of an encounter creature (delete_encounter_creature_inc.php). Standalone row — NOT a
// death-session anchor (no loot/trigger pipeline), so it does not join _DEATH_LOG_ANCHOR_TYPES.
// There is no `creature_deleted` icon; reuse the existing `delete_encounter_creature` glyph.
function renderCreatureFled(_roll: Roll) {
    return `<div class="row m-0"><div class="col-12 text-left">${_icon('fleeing')} has fled the battlefield.</div></div>`;
}

function renderCreatureDeleted(_roll: Roll) {
    return `<div class="row m-0"><div class="col-12 text-left">${_icon('delete_encounter_creature')} is deleted.</div></div>`;
}

function renderStartTurn(roll: Roll)    { return `<div class="text-left col-12">${_icon('started_turn')} Started Turn</div>`; }
// Legendary Turns (plans/legendary-turns-plan.md): same frame wiring as start_turn
// (chime/GM auto-load in start-turn-gm.ts key on this type too) — only the label differs.
function renderStartLegendaryTurn(roll: Roll) { return `<div class="text-left col-12">${_icon('started_turn')} Started <span class="text-warning fw-bold">Legendary Turn</span></div>`; }
function renderDying(roll: Roll)        { return `<div class="row m-0"><div class="col-12 text-left">is Dying...</div></div>`; }
function renderDies(roll: Roll)         { return `<div class="row m-0"><div class="col-12 text-left">Dies...</div></div>`; }
function renderSlippingAway(roll: Roll) { return `<div class="row m-0"><div class="col-12 text-left">is Slipping Away...</div></div>`; }
function renderIsConscious(roll: Roll)  { return `<div class="row m-0"><div class="col-12 text-left">is Conscious!</div></div>`; }
function renderStabilized(roll: Roll)   { return `<div class="row m-0"><div class="col-12 text-left">Stabilized.</div></div>`; }
function renderRecovers(roll: Roll)     { return `<div class="row m-0"><div class="col-12 text-left">Recovers.</div></div>`; }

function renderMassiveDamageInstantDeath(roll: Roll) {
    const byArr = (roll.roll_by_arr as ItemArr | null | undefined) || {};
    const byType = _first(byArr.item_type) || '';
    const suffix = byType === 'player_character'
        ? ' Player characters bypass Dying when this rule applies.'
        : '';
    return `<div class="row m-0"><div class="col-12 text-left">${_icon('creature_killed')} Massive damage: slain outright &mdash; this hit dealt damage at least equal to max vitality.${_esc(suffix)}</div></div>`;
}

function renderStatusEffectApplied(roll: Roll) {
    const t = roll.roll_type_1;
    const itemId   = String(roll.roll_item_id || '');
    const itemType = String(roll.roll_item_id_item_type || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    const invTpl   = String(roll.roll_item_id_inventory_item_id || '');

    const isApplyType = (t === 'status_effect_applied' || t === 'self_status_effect_applied');
    const fromConsumable = isApplyType && _isUuid(invTpl);

    let line1;
    if (fromConsumable) {
        line1 = `<span class="me-1">Consumed</span>
            ${_infoSpan(invTpl, _icon('consume'))}
            ${_infoSpan(invTpl, _esc(itemName), 'link-primary')}`;
    } else {
        const iconKey = (itemType === 'status_effect') ? itemId : itemType;
        line1 = `${_infoSpan(itemId, _icon(iconKey))}
            ${_infoSpan(itemId, _esc(itemName), 'link-primary')}`;
    }

    let middle = '';
    if (!fromConsumable) {
        middle = `<div>${_esc(_ucwords(t))}</div>`;
    }

    return `<div class="text-left col-12 border-bottom pb-2">${line1}</div>${middle}${_roleToBlock(roll)}`;
}

function renderStatusEffectAllConnectionsDeleted(roll: Roll) {
    const itemId   = String(roll.roll_item_id || '');
    const itemType = String(roll.roll_item_id_item_type || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    const iconKey = (itemType === 'status_effect') ? itemId : itemType;
    return `<div class="text-left col-12 border-bottom pb-2">
            ${_infoSpan(itemId, _icon(iconKey))}
            ${_infoSpan(itemId, _esc(itemName), 'link-primary')}
        </div>
        <div class="text-left">${_esc(_ucwords(roll.roll_type_1))}</div>
        ${_roleToBlock(roll)}`;
}

function renderAbilityApplied(roll: Roll) {
    const t = roll.roll_type_1;
    const isConsumable = [
        'elixir_applied', 'potion_applied', 'coating_applied',
        'food_applied', 'drink_applied', 'monster_bit_applied',
    ].indexOf(t) >= 0;

    let linkId = String(roll.roll_item_id || '');
    const invTpl = String(roll.roll_item_id_inventory_item_id || '');
    if (isConsumable && _isUuid(invTpl)) linkId = invTpl;

    const verb = (t === 'food_applied' || t === 'drink_applied' || t === 'monster_bit_applied') ? 'Consumed'
               : (t === 'potion_applied' || t === 'elixir_applied') ? 'Drank'
               : (t === 'coating_applied') ? 'Applied'
               : '';
    const iconKey = verb === 'Consumed' ? 'consume' : (roll.roll_item_id_item_type || '');
    const verbHtml = verb ? `<span class="me-1">${_esc(verb)}</span>` : '';
    const trailing = verb === '' ? ' Applied' : '';

    const roleBlock = _roleToBlock(roll);
    const titleClass = roleBlock ? 'col-12 text-left pb-1 mb-1 border-bottom' : 'col-12 text-left';

    return `<div>
        <div class="${titleClass}">
            ${verbHtml}
            ${_infoSpan(linkId, _icon(iconKey), 'link-primary')}
            ${_infoSpan(linkId, _esc(roll.roll_item_id_item_name || ''), 'link-primary')}${_esc(trailing)}
        </div>
        ${roleBlock}
    </div>`;
}

function renderNoRoll(roll: Roll) {
    const itemId   = String(roll.roll_item_id || '');
    const itemType = String(roll.roll_item_id_item_type || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    const invTpl   = String(roll.roll_item_id_inventory_item_id || '');
    const type2    = String(roll.roll_type_2 || '');

    const isConsume   = (type2 === 'consume');
    const fromInvItem = _isUuid(invTpl);
    const linkId      = (isConsume && fromInvItem) ? invTpl : (itemId || invTpl);
    const iconKey     = isConsume ? 'consume' : (itemType || itemId);
    const verbHtml    = isConsume ? `<span class="me-1">Consumed</span>` : '';

    const nameHtml = itemName
        ? _infoSpan(linkId, _esc(itemName), 'link-primary')
        : `<span class="text-muted">${_esc(_ucwords(type2 || 'no_roll'))}</span>`;

    const roleBlock = _roleToBlock(roll);
    const titleClass = roleBlock ? 'col-12 text-left pb-1 mb-1 border-bottom' : 'col-12 text-left';

    return `<div>
        <div class="${titleClass}">
            ${verbHtml}
            ${_infoSpan(linkId, _icon(iconKey), 'link-primary')}
            ${nameHtml}
        </div>
        ${roleBlock}
    </div>`;
}

function renderPickpocket(roll: Roll) {
    const t = roll.roll_type_1;
    if (t === 'peek_in_bags_failed') {
        return `<div class="text-left col-12">${_icon('pickpocket', 20, 20)} <span class="text-danger">Failed to Peek in Bags</span></div>`;
    }
    if (t === 'peek_in_bags') {
        return `<div class="text-left col-12">${_icon('pickpocket', 20, 20)} <span class="text-success">Peek in Bags</span></div>`;
    }
    if (t === 'pickpocket_failed') {
        return `<div class="text-left col-12">${_icon('pickpocket', 20, 20)} <span class="text-danger">Pickpocket Failed — Target Alerted</span></div>`;
    }
    const itemName = String(roll.roll_item_id_item_name || 'Unknown');
    const qty = parseInt(String(roll.roll_item_id_item_qty || 1), 10);
    const linkId = String(roll.roll_item_id_inventory_item_id || roll.roll_item_id || '');
    const qtyHtml = qty > 1 ? `<span class="badge bg-info">${_esc(qty)}</span> ` : '';
    const nameHtml = _isUuid(linkId)
        ? _infoSpan(linkId, `${_icon(linkId, 20, 20)} <strong>${_esc(itemName)}</strong>`)
        : `<strong>${_esc(itemName)}</strong>`;
    return `<div class="text-left col-12">${_icon('pickpocket', 20, 20)} <span class="text-success">Pickpocket Successful</span> — Stole ${qtyHtml}${nameHtml}</div>`;
}

function renderRangedMiss(roll: Roll) {
    const t = roll.roll_type_1;
    let body;
    if (t === 'ranged_stray_double_natural_one') {
        body = `${_icon('unlucky_roll')} Wild stray shot, then <strong>another natural 1</strong> — a legendary whiff. (The loose shot still did not connect.)`;
    } else if (t === 'attack_automatically_misses_stray') {
        body = `${_icon('unlucky_roll')} <strong>Critical miss</strong> — the attack goes wide, sending a stray shot along its fire path. (The loose shot still did not connect.)`;
    } else if (t === 'ranged_attack_missed_fire_path') {
        const missedName = _esc(roll.roll_to_item_name || 'the target');
        body = `${_icon('unlucky_roll')} The attack <strong>missed</strong> ${missedName} — the shot flies past along its fire path, risking hitting another creature in its trajectory.`;
    } else {
        return '';
    }
    return `<div class="text-left col-12">${body}</div>${_roleToBlock(roll)}`;
}

function renderMerchantInventoryTeleported(roll: Roll) {
    return `<div class="row m-0"><div class="col-12 text-left">${_icon('merchant')} Merchant's wares shimmer and vanish &mdash; teleportation contingency triggered.</div></div>`;
}

const MOVE_TO_DEST_ICONS: Record<string, string> = {
    encounter_loaded:                    'encounter_loaded',
    party_pushed_to_encounter:           'push_party_to_encounter',
    targets_pushed_to_encounter:         'push_targets_to_encounter',
    all_pushed_to_encounter:             'push_all_creatures_to_encounter',
    all_creatures_pulled_from_encounter: 'all_creatures_pulled_from_encounter',
    moved_to_encounter:                  'moved_to_encounter',
    target_moved_to_encounter:           'target_moved_to_encounter',
    target_moved_to_room:                'target_moved_to_encounter',
    party_pushed_to_campsite:            'set_campaign_campsite',
    party_pushed_to_stronghold:          'set_campaign_stronghold',
    player_joined_campaign:              'moved_to_encounter',
};

function renderPartyMoveToDestination(roll: Roll) {
    const t        = roll.roll_type_1;
    const to: ItemArr = (roll.roll_to_arr as ItemArr | null | undefined) || {};

    // Encounter destinations no longer carry top-level item_id/item_name/item_type on
    // roll_to_arr — the 2026-06-09 encounter identity-bridge refactor moved them into the
    // `encounters` bucket (item_field() infers them server-side). Reading them off the raw
    // arr here returned empty, so the header showed no name and the thumbnail body was
    // suppressed. Prefer the server-denormalized roll_to_* columns (correct for every
    // destination type); fall back to the top-level shape for legacy non-encounter arrs.
    const destId   = String(roll.roll_to           || _first(to.item_id)   || '');
    const destName = String(roll.roll_to_item_name || _first(to.item_name) || '');
    const destType = String(roll.roll_to_item_type || _first(to.item_type) || '');

    const iconKey  = MOVE_TO_DEST_ICONS[t] || t;

    // When there's an encounter thumbnail in the body, surface the destination name
    // centered above it instead of inline next to the header icon — easier to scan
    // multiple GM-transfer rows back-to-back. Non-encounter destinations have no
    // thumbnail body, so the name stays inline in the header.
    const hasThumbnailBody = (destType === 'encounter');

    const inlineNameLink = (!hasThumbnailBody && destId && destName)
        ? ` <span class="link-primary ms-1" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(destId)}">${_esc(destName)}</span>`
        : '';

    const header = `<div class="text-left col-12 border-bottom pb-2">${_icon(iconKey)} ${_esc(_ucwords(t))}${inlineNameLink}</div>`;

    let body = '';
    if (hasThumbnailBody) {
        const centeredName = (destId && destName)
            ? `<div class="text-center col-12 pt-2"><span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(destId)}">${_esc(destName)}</span></div>`
            : '';
        // renderLogLineThumbnail reads top-level item_id/item_name/pic_url — also absent on
        // the encounter roll_to_arr. Normalize from the roll_to_* columns so the destination
        // thumbnail (encounter pic, or initials + info link) renders instead of going blank.
        const thumbArr = {
            ...to,
            item_id:   destId,
            item_name: destName,
            item_type: destType,
            pic_url:   roll.roll_to_pic_url,
        };
        const thumb = roll.roll_to
            ? `<div class="d-flex align-items-center justify-content-center col-12 pt-2" data-ft-log-target="1">${renderLogLineThumbnail(thumbArr, { thumbnail_link: 1 })}</div>`
            : '';
        body = `${centeredName}${thumb}`;
    }

    return `${header}${body}`;
}

function renderInventoryAction(roll: Roll) {
    const t = roll.roll_type_1;
    const itemId   = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    const result   = parseInt(String(roll.roll_result || 0), 10);
    const qtyBadge = (result && result !== 1)
        ? `<span class='badge bg-dark border'>${_esc(result)}</span> `
        : '';
    return `<div class="text-left">
        <div class="border-bottom pb-1 mb-1 col-12">
            ${_icon(t)} ${qtyBadge}
            <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_esc(itemName)}</span>
        </div>
        <div class="d-flex align-items-center justify-content-center col-12">
            ${renderLogLineThumbnail(roll.roll_item_arr || {}, { thumbnail_link: 1 })}
            <div class="p-2">${_icon(t)}</div>
            <div class="d-flex align-items-center" data-ft-log-target="1">${renderLogLineThumbnail(roll.roll_to_arr || {}, { thumbnail_link: 1 })}</div>
        </div>
    </div>`;
}

function renderInventoryDeleted(roll: Roll) {
    const t  = roll.roll_type_1;
    const t2 = String(roll.roll_type_2 || '');
    const itemId   = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    const result   = parseInt(String(roll.roll_result || 0), 10);
    const qtyBadge = (result && result !== 1)
        ? `<span class='badge bg-dark border'>${_esc(result)}</span> `
        : '';

    const isDestroyType = (t === 'inventory_destroyed_rubble'
        || t === 'inventory_destroyed_vanished'
        || t === 'inventory_container_spilled'
        || t === 'corpse_destroyed'
        || t === 'corpse_decayed');

    if (isDestroyType) {
        let headerIconKey = 'inventory_deleted';
        let headerLabel = 'Destroyed';
        if (t === 'inventory_destroyed_rubble') {
            headerIconKey = 'rubble';
            headerLabel = 'Turned to Rubble';
        } else if (t === 'inventory_container_spilled') {
            headerLabel = 'Container spilled';
        } else if (t === 'corpse_destroyed') {
            headerLabel = 'Body destroyed'; // corpse-as-inventory 2026-09-23: its gear spilled, no revive
        } else if (t === 'corpse_decayed') {
            headerLabel = 'Body rotted away'; // Rotting ran out: bones remain, gear spilled
        }
        return `<div class="text-left">
            <div class="border-bottom pb-1 mb-1 col-12">
                ${_icon(headerIconKey)}
                <span class="text-muted">${_esc(headerLabel)}</span>
            </div>
            <div class="col-12 text-center">
                ${qtyBadge}
                <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_esc(itemName)}</span>
            </div>
            <div class="d-flex align-items-center justify-content-center col-12 mt-1" data-ft-log-target="1">
                ${renderLogLineThumbnail(roll.roll_to_arr || roll.roll_item_arr || {}, { thumbnail_link: 1 })}
            </div>
        </div>`;
    }

    const consumed = (t === 'inventory_deleted' && (t2 === 'consumed' || t2 === 'consumed_unit_per_use'));
    const rotted = (t === 'inventory_deleted' && t2 === 'rotted');
    const unitPerUse = (consumed && t2 === 'consumed_unit_per_use');
    const leadingIcon = rotted
        ? `<span class="me-1">Rotted</span> ${_icon('spoiling')}`
        : consumed
            ? (unitPerUse
                ? `<span class="me-1">Used</span> ${_icon('minus')}`
                : `<span class="me-1">Consumed</span> ${_icon('consume')}`)
            : _icon(t);

    return `<div class="text-left">
        <div class="border-bottom pb-1 mb-1 col-12">
            ${leadingIcon}
            ${qtyBadge}
            <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_esc(itemName)}</span>
        </div>
        <div class="d-flex align-items-center justify-content-center col-12">
            ${renderLogLineThumbnail(roll.roll_item_arr || {}, { thumbnail_link: 1 })}
        </div>
    </div>`;
}

function renderAwardedExperienceMarks(roll: Roll) {
    const marks = roll.roll_result;
    return `<div class="text-left">
        <div class="border-bottom pb-1 mb-1 col-12">
            ${_icon('awarded_experience_marks')} Awarded <span class="badge bg-light text-dark border">${_esc(marks)}</span> Experience Marks!
        </div>
        <div class="d-flex align-items-center justify-content-center col-12" data-ft-log-target="1">
            ${renderLogLineThumbnail(roll.roll_to_arr || {}, { thumbnail_link: 1 })}
        </div>
    </div>`;
}

// GM Award Luck: header line + the creature that gained luck. The backend
// (gm_award_luck_inc) passes the awarded creature as roll_to_arr and the delta
// (+1) as roll_result. Was previously a SIMPLE_MESSAGE_TYPE, so the target
// thumbnail never rendered — the row read just "Gm Award Luck" with no creature.
function renderGmAwardLuck(roll: Roll) {
    const delta = Number(roll.roll_result ?? 0);
    const badge = delta > 0 ? `+${delta}` : String(delta);
    return `<div class="text-left">
        <div class="border-bottom pb-1 mb-1 col-12">
            ${_icon('gm_award_luck')} Awarded <span class="badge bg-light text-dark border">${_esc(badge)}</span> Luck
        </div>
        <div class="d-flex align-items-center justify-content-center col-12" data-ft-log-target="1">
            ${renderLogLineThumbnail(roll.roll_to_arr || {}, { thumbnail_link: 1 })}
        </div>
    </div>`;
}

function renderCanLevelUp(roll: Roll) {
    return `<div class="text-left">
        <div class="border-bottom pb-1 mb-1 col-12">
            ${_icon('level_up')} Can Level Up!
        </div>
        <div class="d-flex align-items-center justify-content-center col-12" data-ft-log-target="1">
            ${renderLogLineThumbnail(roll.roll_to_arr || {}, { thumbnail_link: 1 })}
        </div>
    </div>`;
}

function renderRandomTable(roll: Roll) {
    const effect      = roll.roll_random_table_effect ?? null;
    const description = roll.roll_random_table_description ?? null;
    const attachedId  = roll.roll_random_table_attached_item_id ?? null;
    const attachedNm  = roll.roll_random_table_attached_item_name ?? null;
    const attachedTy  = String(roll.roll_random_table_attached_item_type ?? '');

    // No early '' return on missing snapshot fields: the first WS push arrives
    // before the Phase-2d snapshot UPDATE lands, and returning '' left the whole
    // roll invisible. The header + big-result roll number render from the roll
    // row itself; effect/description fill in on the next buildLog refresh.

    const t           = String(roll.roll_type_1 || '');
    const tableId     = String(roll.roll_item_id || '');
    const tableName   = String(roll.roll_item_id_item_name || '');
    const baseUrl     = (typeof BASE_URL !== 'undefined') ? BASE_URL : '/';
    const headerIcon  = (t === 'random_name_table') ? 'random_name_table'
        : (t === 'random_loot_table') ? 'random_loot_table'
        : (t === 'random_encounter_table') ? 'random_encounter_table'
        : 'random_table';

    // The rolled RESULT line: the attached item's icon + clickable name (openItemInfo,
    // like skill/ability rows), NOT the raw `effect` field — Daily Spin rows all store
    // effect="a" placeholder junk. Fall back to effect only for name-less, item-less rows.
    let resultLine = '';
    if (attachedId) {
        const label = attachedNm || effect || attachedId;
        // The client can't icon a bare UUID (→ "?"). Use the attached item's TYPE for the icon,
        // except status_effect items which DO have a per-id rune. Mirrors renderStatusEffectApplied.
        const iconKey = (attachedTy === 'status_effect') ? attachedId
            : (attachedTy || 'random_table');
        resultLine = `<span role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(attachedId)}">${_icon(iconKey)}</span>
            <a class="link-primary fw-semibold ms-1" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(attachedId)}">${_esc(label)}</a>`;
    } else if (effect) {
        resultLine = parseText(effect);
    }

    // Description sits BELOW its own border, mirroring the ability/status log rows
    // (header → result → separated description), instead of being crammed inline.
    const descBlock = description
        ? `<div class="row m-0"><div class="col-12 text-left border-top pt-1 mt-1">${parseText(description)}</div></div>`
        : '';
    const resultBlock = resultLine
        ? `<div class="row m-0"><div class="col-12 text-left">${resultLine}</div></div>`
        : '';

    return {
        showBigResult: true,
        body: `<div class="row m-0">
        <div class="col-12 text-left border-bottom pb-1 mb-1">
            ${_icon(headerIcon)}
            <a target='_blank' href='${baseUrl}item.php?id=${_esc(tableId)}'><em><strong>${_esc(tableName)}</strong></em></a>
        </div>
    </div>
    ${resultBlock}
    ${descBlock}`,
    };
}

function renderCreatureTransform(roll: Roll) {
    return `<div class="row m-0"><div class="col-12 text-left">${_icon('transforms')} Transforms!</div></div>`;
}

function renderStartTraining(roll: Roll) {
    const skillId   = String(roll.roll_item_id || '');
    const skillName = String(roll.roll_item_id_item_name || 'skill');
    const trCatalog = (typeof ROLLTYPE_CATALOG !== 'undefined' ? ROLLTYPE_CATALOG : null);
    const trRollId  = String(trCatalog?.training_roll?.item_id || '');
    const headerIconClick = trRollId
        ? `<span role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(trRollId)}">${_icon(trRollId)}</span>`
        : _icon('training_roll');
    const headerTxtClick = trRollId
        ? `<span class="fw-semibold ms-1" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(trRollId)}">Begin Training</span>`
        : `<span class="fw-semibold ms-1">Begin Training</span>`;
    let skillRow = '';
    if (skillId !== '') {
        skillRow = `<div class="text-left">
            <span role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(skillId)}">${_icon(skillId)}</span>
            <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(skillId)}">${_esc(skillName)}</span>
            <span class="text-muted"> — 1 day</span>
        </div>`;
    }
    return `<div>
        <div class="text-left border-bottom pb-1 mb-1">${headerIconClick} ${headerTxtClick}</div>
        ${skillRow}
    </div>`;
}

function renderTrainingSkillTierUp(roll: Roll) {
    const t = roll.roll_type_1;
    const skillId   = String(roll.roll_item_id || '');
    const skillName = String(roll.roll_item_id_item_name || 'skill');
    const tierKey   = String(roll.roll_type_2 || '');

    const trCatalog = (typeof ROLLTYPE_CATALOG !== 'undefined' ? ROLLTYPE_CATALOG : null);
    const trRollId  = String(trCatalog?.training_roll?.item_id || '');

    let headerIcon;
    if (t === 'training_skill_tier_up' && trRollId !== '') {
        headerIcon = `<span role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(trRollId)}">${_icon(trRollId)}</span>`;
    } else if (t === 'ascension_skill_tier_up') {
        headerIcon = _icon('ascension_skill_point');
    } else {
        headerIcon = '';
    }

    let skillBlock = '';
    if (skillId !== '') {
        skillBlock = `<span role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(skillId)}">${_icon(skillId)}</span>
            <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(skillId)}">${_esc(skillName)}</span>`;
    }

    let tierBlock = '';
    if (tierKey !== '') {
        tierBlock = `<span>${_icon(tierKey)}</span>`;
    }

    return `<div>
        <div class="text-left border-bottom pb-1 mb-1">${headerIcon} <span class="fw-semibold ms-1">New Skill Tier</span></div>
        <div class="text-left d-flex flex-wrap align-items-center gap-2 mb-1">${skillBlock} ${tierBlock}</div>
    </div>`;
}

interface SkillTierProgress {
    at_max: boolean;
    pct: number;
    current_req: number;
    next_req: number | null;
    sp_total: number;
}
function _ascSpProgress(spTotal: unknown): SkillTierProgress | null {
    const cat = (typeof SKILLTIER_CATALOG !== 'undefined') ? SKILLTIER_CATALOG : null;
    if (!cat) return null;
    const sp = Math.max(0, parseInt(String(spTotal || 0), 10));
    const byLevel: Record<number, Record<string, unknown>> = {};
    let maxLevel = 0;
    for (const key in cat) {
        const entry = cat[key] as Record<string, unknown> | undefined;
        if (!entry) continue;
        const lvl = parseInt(String(entry.skill_tier_level || 0), 10);
        byLevel[lvl] = entry;
        if (lvl > maxLevel) maxLevel = lvl;
    }
    let curLevel = 0;
    for (let lvl = maxLevel; lvl >= 0; lvl--) {
        const e = byLevel[lvl];
        if (e && sp >= parseInt(String(e.sp_required || 0), 10)) {
            curLevel = lvl;
            break;
        }
    }
    const curReq = parseInt(String((byLevel[curLevel] || {}).sp_required || 0), 10);
    if (curLevel >= maxLevel) {
        return { at_max: true, pct: 100, current_req: curReq, next_req: null, sp_total: sp };
    }
    const nextReq = parseInt(String((byLevel[curLevel + 1] || {}).sp_required || 0), 10);
    const span    = nextReq - curReq;
    if (span <= 0) {
        return { at_max: true, pct: 100, current_req: curReq, next_req: null, sp_total: sp };
    }
    const frac = (Math.min(sp, nextReq) - curReq) / span;
    const pct  = Math.max(0, Math.min(100, Math.floor(100 * frac)));
    return { at_max: false, pct, current_req: curReq, next_req: nextReq, sp_total: sp };
}

function renderAscensionSkillPointRoll(roll: Roll) {
    const t         = roll.roll_type_1;
    const ok        = (t === 'gained_ascension_skill_point');
    const skillId   = String(roll.roll_item_id || '');
    const skillName = String(roll.roll_item_id_item_name || '');
    const dc        = parseInt(String(roll.roll_dc || 0), 10);
    const mod       = parseInt(String(roll.roll_mod || 0), 10);
    const total     = parseInt(String(roll.roll_result || 0), 10);

    let natural    = null;
    let spTotal    = null;
    const rfRaw = roll.rolls_full;
    let rf = null;
    if (typeof rfRaw === 'string' && rfRaw !== '') {
        try { rf = JSON.parse(rfRaw); } catch (e) { rf = null; }
    } else if (Array.isArray(rfRaw)) {
        rf = rfRaw;
    }
    if (Array.isArray(rf)) {
        for (const tuple of rf) {
            if (!Array.isArray(tuple) || tuple.length < 2) continue;
            const label = String(tuple[1] || '');
            if (label === 'd20_1' && natural === null) {
                natural = parseInt(String(tuple[0]), 10);
            } else if (label === 'sp_total_after' && spTotal === null) {
                spTotal = parseInt(String(tuple[0]), 10);
            }
        }
    }
    if (natural === null) {
        natural = total - mod;
    }

    let d20Bg = 'bg-primary';
    if (natural === 20)      d20Bg = 'bg-success';
    else if (natural === 1)  d20Bg = 'bg-danger';

    let skillBlock = '';
    if (skillId !== '') {
        skillBlock = `<span class="text-muted small"> — </span>
            <span role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(skillId)}">${_icon(skillId)}</span>
            <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(skillId)}">${_esc(skillName)}</span>`;
    }

    const inlineSuccess = (ok && dc > 0)
        ? ` <span class="text-muted">—</span> <span class="text-success fw-semibold">Success.</span>`
        : '';

    let dcBlock = '';
    if (dc > 0) {
        dcBlock = `<div class="text-left mb-2">
            <span class="fw-semibold">${total}</span><span class="text-muted"> vs. </span><span
                class="badge bg-warning text-dark"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-original-title="Skill tier die (meet or exceed)"
            >DC ${dc}</span>${inlineSuccess}
        </div>`;
    }

    let verdictBlock;
    if (ok) {
        let barBlock = '';
        if (spTotal !== null) {
            const prog = _ascSpProgress(spTotal);
            if (prog) {
                const barTitle = prog.at_max
                    ? `${prog.sp_total} SP — maximum tier from skill points`
                    : `${prog.sp_total} / ${prog.next_req} SP · ${prog.pct}% toward next tier`;
                const barTitleEsc = _esc(barTitle);
                barBlock = `<div class="text-left mb-1">
                    <div
                        class="w-100 py-1"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-bs-original-title="${barTitleEsc}"
                    >
                        <div class="progress" style="height: 10px;">
                            <div
                                class="progress-bar bg-info"
                                role="progressbar"
                                style="width: ${prog.pct}%;"
                                aria-valuenow="${prog.pct}"
                                aria-valuemin="0"
                                aria-valuemax="100"
                                aria-label="${barTitleEsc}"
                            ></div>
                        </div>
                    </div>
                </div>`;
            }
        }
        const successFallback = (dc > 0)
            ? ''
            : `<div class="text-left mb-1"><span class="text-success fw-semibold">Success.</span></div>`;
        verdictBlock = `${successFallback}${barBlock}`;
    } else {
        verdictBlock = `<div class="text-left mb-1"><span class="text-danger fw-semibold">Failure.</span><span class="text-muted"> Confirmation roll did not meet the skill tier.</span></div>`;
    }

    return `<div>
        <div class="text-left border-bottom pb-1 mb-1">
            ${_icon('ascension_skill_point')}
            <span class="fw-semibold">Ascension skill point roll</span>
            ${skillBlock}
        </div>
        <div class="text-left mb-1">
            <span
                class="badge ${d20Bg} d-inline-block"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-original-title="d20"
            >${natural}</span>
            <span class="text-muted fw-light mx-1">+</span>
            <span
                class="badge bg-warning d-inline-block"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-original-title="modifiers"
            >${mod}</span>
            <span class="text-muted fw-light mx-1">=</span>
            <span class="fw-semibold">${total}</span>
        </div>
        ${dcBlock}
        ${verdictBlock}
    </div>`;
}

function renderFinishReading(roll: Roll) {
    const readerId   = String(roll.roll_by || '');
    const readerName = String(roll.roll_by_item_name || 'Someone');
    const bookId     = String(roll.roll_item_id || '');
    const bookName   = String(roll.roll_item_id_item_name || 'a book');
    return `<div>
        <div class="text-left border-bottom pb-1 mb-1">${_icon('read')} <span class="fw-semibold">Finished reading</span></div>
        <div class="text-left">
            <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(readerId)}">${_esc(readerName)}</span><span class="text-muted"> finished </span><span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(bookId)}">${_esc(bookName)}</span><span class="text-muted"> and gained training from the book.</span>
        </div>
    </div>`;
}

function renderTransferDamage(roll: Roll) {
    const t           = roll.roll_type_1;
    const statusId    = String(roll.roll_type_2 || '');
    const statusName  = String(roll.roll_type_2_name || statusId || 'status');
    return {
        showBigResult: true,
        body: `<div class="text-left border-bottom pb-1 mb-1 col-12">
            ${_icon(t)}
            <span class="badge bg-warning text-dark border" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(statusId)}">${_esc(statusName)}</span>
        </div>
        <div class="d-flex align-items-center justify-content-center col-12" data-ft-log-target="1">
            ${renderLogLineThumbnail(roll.roll_to_arr || {}, { thumbnail_link: 1 })}
        </div>`,
    };
}

function renderDisarmed(roll: Roll) {
    const t        = roll.roll_type_1;
    const itemId   = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    const result   = parseInt(String(roll.roll_result || 0), 10);
    const qtyBadge = (result && result !== 1)
        ? `<span class='badge bg-dark border'>${_esc(result)}</span> `
        : '';
    return `<div class="text-left">
        <div class="border-bottom pb-1 mb-1 col-12">
            ${_icon(t)} ${qtyBadge}
            <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_esc(itemName)}</span>
        </div>
        <div class="d-flex align-items-center justify-content-center col-12" data-ft-log-target="1">
            ${renderLogLineThumbnail(roll.roll_to_arr || {}, { thumbnail_link: 1 })}
        </div>
    </div>`;
}

function renderDeathAnchorOptimistic(roll: Roll) {
    return renderDeathSessionBody(roll, [], { hideToggleWhenEmpty: true }).html;
}

// ── Trap verbs (2026-08-03) ────────────────────────────────────────────────────────────────
// Every trap roll_type_1 was written to `rolls` but NONE of them was ever registered here, and
// renderSimpleLogRow returns '' for an unhandled type — so a trap firing, arming, being discerned
// or being disarmed printed literally nothing at the table ("minigame works but no update in the
// log", owner report 2026-08-03). The icons were already minted (icon-data/overrides.ts), only
// the dispatch entry was missing.
//
// roll_item_id points at the trap_ability for a firing and at the HOST inventory row for the
// arm/disarm verbs, so the name link is meaningful either way; roll_to carries the victim (fire)
// or the host (the rest) and rides the standard role-to thumbnail block.
const TRAP_EVENT_VERBS: Record<string, string> = {
    trap_triggered:     'A trap sprang!',
    trap_armed:         'Trap armed',
    trap_disarmed:      'Trap disarmed',
    trap_disarm_failed: 'Disarm failed — the trap is still set',
    trap_discerned:     'Trap-work understood',
    trap_mounted:       'Trap payload mounted',
};

function renderTrapEvent(roll: Roll) {
    const t        = String(roll.roll_type_1 || '');
    const itemId   = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    const verb     = TRAP_EVENT_VERBS[t] || _ucwords(t);
    const nameHtml = (itemName && _isUuid(itemId))
        ? ` <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_esc(itemName)}</span>`
        : (itemName ? ` ${_esc(itemName)}` : '');
    return `<div class="text-left col-12 border-bottom pb-2">${_icon(t)} ${_esc(verb)}${nameHtml}</div>`
        + _roleToBlock(roll);
}

// ── MACHINIST ORB QUEUE EVENTS ─────────────────────────────────────────────────────────────────
// janus/encounter/orb_queue.php writes an orb_channeled / orb_evoked row on EVERY channel and
// every roll-off, carrying the OrbType item_id on roll_item_id and its name ("Fire Orb") on
// roll_item_id_item_name. There was no HANDLERS entry, so renderSimpleLogRow() returned '' and the
// row was DROPPED — the whole mechanic was silent at the table: no way to see when an orb was
// channeled, or which one just fired.
//
// The orb types are catalog-only constants (additions.php OrbType::LIST), NOT database items — they
// have no icon-registry entry and no info panel. So this renders neither _icon(itemId) (would be a
// "?" placeholder) nor _infoSpan (a dead link). A colored pip carries the identity instead, using
// the SAME palette as the sheet strip (weapon-abilities.ts orbQueueHtml) and the floating token arc,
// so one glance ties the log line to the orb sitting above the token. The header glyph is the Orb
// Slots mechanic icon, which IS registered.
const ORB_TYPE_COLORS: Record<string, string> = {
    '475DC2B1-D30C-44B9-884A-C02A7171E097': '#f5d90a', // Lightning Orb
    '29C4389B-B60D-409C-A4F8-A00BA5D3145D': '#6ec6ff', // Cold Orb
    'EBF82DF9-0CD2-4417-967F-454D67917902': '#8b2fd6', // Necrotic Orb
    '00EF99B0-1906-4C43-BC01-33C84A0A470D': '#4757d6', // Thunder Orb
    '1F4F3172-2CE9-4DC2-82B0-2280155608FD': '#ff7a1a', // Fire Orb
    '1114078A-EA85-49F8-A649-688A4D084577': '#c08cff', // Force Orb
    '11ECD9B7-F7D0-41DF-986D-1A2AD68DF35C': '#35c235', // Poison Orb
    '025EC787-14C9-4910-B375-3D99E1F683D0': '#9acd32', // Acid Orb
};

function renderOrbQueueEvent(roll: Roll) {
    const t        = String(roll.roll_type_1 || '');
    const itemId   = String(roll.roll_item_id || '').toUpperCase();
    const itemName = String(roll.roll_item_id_item_name || '').trim();
    const color    = ORB_TYPE_COLORS[itemId] || '#c9d4e0';
    const pip = `<span aria-hidden="true" style="display:inline-block;width:14px;height:14px;`
        + `border-radius:50%;vertical-align:-2px;margin-right:.25rem;`
        + `background:radial-gradient(circle at 35% 30%, #ffffffcc 0%, ${color} 45%, ${color}33 100%);`
        + `box-shadow:0 0 5px 1px ${color}88;"></span>`;
    const nameHtml = itemName ? ` &mdash; ${pip}<span class="fw-bold">${_esc(itemName)}</span>` : '';
    return `<div class="text-left col-12">${_icon('orb_slots', 20, 20)} ${_esc(_ucwords(t))}${nameHtml}</div>`;
}

// ===================================================================
// Dispatch table
// ===================================================================
const HANDLERS: Record<string, RollHandler> = {};

HANDLERS.surface_reacted = (roll: Roll) => `<div class="text-left col-12">${_esc(typeof roll.rolls_full === 'string' ? roll.rolls_full : 'Surface transformed')}</div>`;

HANDLERS.surface_entry_skipped = (roll: Roll) => `<div class="text-left col-12" data-ft-surface-entry-skipped>Surface entry check not repeated: an earlier check may be outside the held combat log.</div>${_roleToBlock(roll)}`;

HANDLERS.janus_unported_skip = (roll: Roll) => {
    const reason = typeof roll.rolls_full === 'string' ? roll.rolls_full : '';
    if (/^(?:preflight:)?timed-finish:/.test(reason)
        || reason.includes('; loss-triggers, shapeshift/special-status cleanup, summon-tag cleanup, expiry log')) {
        return '<div class="text-left col-12">This timed activity could not finish. Check its requirements and try again; reload the page if character or item details are missing.</div>';
    }
    return `<div class="text-left col-12">Not yet supported in the browser — skipped: ${_esc(reason)}</div>`;
};

HANDLERS.wind_wall_lift = (roll: Roll) => {
    let feet = Math.max(0, Number(roll.roll_result) || 0);
    try {
        const path = typeof roll.rolls_full === 'string' ? JSON.parse(roll.rolls_full) : null;
        if (path?.schema === 'ft.wind-lift.v1' && Number.isFinite(path.apexZ) && Number.isFinite(path.from?.z)) {
            feet = Math.max(0, path.apexZ - path.from.z);
        }
    } catch { /* Older log rows may only carry the rounded result. */ }
    return `<div data-ft-wind-wall-lift>Wind Wall lifts ${_esc(Number(feet.toFixed(2)))} ft</div>${_roleToBlock(roll)}`;
};

SIMPLE_MESSAGE_TYPES.forEach(t => { HANDLERS[t] = renderSimpleMessage; });

Object.keys(TRAP_EVENT_VERBS).forEach(t => { HANDLERS[t] = renderTrapEvent; });

HANDLERS.prepare_spell_list        = renderPrepareSpellList;
HANDLERS.reset_prepared_spell_list = renderPrepareSpellList;

HANDLERS.orb_channeled = renderOrbQueueEvent;
HANDLERS.orb_evoked    = renderOrbQueueEvent;

HANDLERS.creature_killed         = renderDeathAnchorOptimistic;
HANDLERS.creature_killed_forever = renderDeathAnchorOptimistic;
HANDLERS.creature_fled           = renderCreatureFled;
HANDLERS.creature_destroyed      = renderDeathAnchorOptimistic;
HANDLERS.creature_body_vanishes  = renderDeathAnchorOptimistic;
HANDLERS.creature_deleted        = renderCreatureDeleted;
HANDLERS.stealth_spotted_gm_prompt = renderStealthSpottedGmPrompt;
HANDLERS.creature_resurrected    = renderCreatureStateIconText;
HANDLERS.dying                   = renderDying;
HANDLERS.dies                    = renderDeathAnchorOptimistic;
HANDLERS.slipping_away           = renderSlippingAway;
HANDLERS.is_conscious            = renderIsConscious;
HANDLERS.stabilized              = renderStabilized;
HANDLERS.recovers                = renderRecovers;
HANDLERS.massive_damage_instant_death = renderMassiveDamageInstantDeath;
HANDLERS.merchant_inventory_teleported = renderMerchantInventoryTeleported;
HANDLERS.start_turn              = renderStartTurn;
HANDLERS.start_legendary_turn    = renderStartLegendaryTurn;
HANDLERS.gm_changed_encounter_light_type = renderGmChangedEncounterLightType;
HANDLERS.gm_changed_encounter_weather    = renderGmChangedEncounterWeather;
// Override the simple-message default so the ended spell's name rides along (see renderEndConcentration).
HANDLERS.end_concentration       = renderEndConcentration;

[
    'status_effect_applied', 'self_status_effect_applied',
    'persistent_aoe_encounter', 'persistent_aoe_attached',
    'target_immune_to_status_effect', 'invalid_target_for_status_effect',
    'self_status_effect_deleted', 'self_status_effect_modified',
    'self_status_effect_all_connections_deleted',
].forEach(t => { HANDLERS[t] = renderStatusEffectApplied; });
HANDLERS.status_effect_all_connections_deleted = renderStatusEffectAllConnectionsDeleted;

HANDLERS.no_roll = renderNoRoll;

function renderStatusEffectRemoved(roll: Roll) {
    const itemId   = String(roll.roll_item_id || '');
    const itemType = String(roll.roll_item_id_item_type || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    const iconKey = (itemType === 'status_effect') ? itemId : itemType;
    return `<div class="text-left col-12 border-bottom pb-2">
            <span role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_icon(iconKey)}</span>
            <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_esc(itemName)}</span>
        </div>
        <div>${_esc(_ucwords(roll.roll_type_1))}</div>
        ${_roleToBlock(roll)}`;
}
[
    'status_effect_removed',
    'status_effect_removed_from_caster_on_hit', 'status_effect_removed_from_target_on_hit',
    'status_effect_removed_from_caster_after_cast', 'status_effect_removed_from_target_after_cast',
    'status_effect_removed_from_caster_after_contesting_roll_failed',
    'status_effect_removed_after_contesting_roll_passed',
    'status_effect_applied_to_caster_on_hit', 'status_effect_applied_to_target_on_hit',
    'status_effect_applied_to_caster_after_cast', 'status_effect_applied_to_target_after_cast',
    'passive_ability_applied_to_caster_after_cast', // Psion Pyrokinetic Fortress (2026-10-07): the caster's passive status row
    'status_effect_applied_to_target_after_contesting_roll_failed',
    'status_effect_applied_to_caster_after_contesting_roll_failed',
    'diseased_applied_to_target_after_contesting_roll_failed',
    'random_disease_applied_to_target_after_contesting_roll_failed',
    'teleport_cleared_conditions',
].forEach(t => { HANDLERS[t] = renderStatusEffectRemoved; });

[
    'spell_applied', 'passive_ability_applied', 'full_ability_applied', 'ability_applied',
    'quick_ability_applied', 'attack_ability_applied', 'free_ability_applied',
    'reaction_ability_applied', 'opportunity_attack_ability_applied',
    'curse_applied', 'menta_applied', 'sanguine_applied',
    'elixir_applied', 'potion_applied', 'coating_applied',
    'food_applied', 'drink_applied', 'monster_bit_applied',
    'custom_full_ability', 'custom_ability', 'custom_quick_ability',
    'custom_free_ability', 'custom_reaction_ability',
].forEach(t => { HANDLERS[t] = renderAbilityApplied; });

HANDLERS.pickpocket        = renderPickpocket;
HANDLERS.pickpocket_failed = renderPickpocket;
HANDLERS.peek_in_bags      = renderPickpocket;
HANDLERS.peek_in_bags_failed = renderPickpocket;

HANDLERS.ranged_stray_double_natural_one = renderRangedMiss;
HANDLERS.attack_automatically_misses_stray = renderRangedMiss;
HANDLERS.ranged_attack_missed_fire_path = renderRangedMiss;

HANDLERS.gm_pushed_campaign_time       = renderCampaignTimePushPull;
HANDLERS.gm_pulled_campaign_time       = renderCampaignTimePushPull;
HANDLERS.campaign_downtime_time_pushed = renderCampaignTimePushPull;

// Player → GM "please pause the clock" ping (request_campaign_time_pause_inc.php). The actor
// (the requesting player) is rendered by _wrapRow's roll_by thumbnail column, so the body is just
// the request sentence.
function renderPlayerRequestedCampaignTimePause(_roll: Roll) {
    return `<div class="text-left col-12">${_icon('pause')} requests campaign time be paused</div>`;
}
HANDLERS.player_requested_campaign_time_pause = renderPlayerRequestedCampaignTimePause;

SCALAR_BIG_RESULT_TYPES.forEach(t => { HANDLERS[t] = renderScalarBigResult; });

[
    'equip_inventory', 'move_inventory', 'equip_both_hands', 'unequip_both_hands',
    'drop_inventory', 'throw_inventory', 'give_inventory', 'impale_inventory', 'add_inventory',
    'finish_donning', 'finish_doffing',
].forEach(t => { HANDLERS[t] = renderInventoryAction; });

[
    'inventory_deleted', 'inventory_updated',
    'inventory_destroyed_rubble', 'inventory_destroyed_vanished',
    'inventory_container_spilled', 'corpse_destroyed', 'corpse_decayed',
    'lockpicks_broke',
].forEach(t => { HANDLERS[t] = renderInventoryDeleted; });

HANDLERS.disarmed = renderDisarmed;

HANDLERS.awarded_experience_marks = renderAwardedExperienceMarks;
HANDLERS.can_level_up             = renderCanLevelUp;
HANDLERS.gm_award_luck            = renderGmAwardLuck;

[
    'encounter_loaded',
    'party_pushed_to_encounter',
    'targets_pushed_to_encounter',
    'all_pushed_to_encounter',
    'all_creatures_pulled_from_encounter',
    'moved_to_encounter',
    'target_moved_to_encounter',
    'target_moved_to_room',
    'party_pushed_to_campsite',
    'party_pushed_to_stronghold',
    'player_joined_campaign',
].forEach(t => { HANDLERS[t] = renderPartyMoveToDestination; });

function renderDamageOrWeapon(roll: Roll) {
    const t = roll.roll_type_1;
    const itemName = String(roll.roll_item_id_item_name || '');
    const interpret = interpretRollResultArr(roll, Number(roll.campaign_permission || (window.FateTide?.user?.isGM ? 1 : 0)));
    return {
        showBigResult: true,
        body: `<div class="row m-0">
            <div class="col-12 text-left border-bottom pb-1">
                ${_icon(t)}
                <span class="badge bg-secondary">${_esc(itemName)}</span>
            </div>
        </div>
        ${interpret}`,
    };
}
HANDLERS.damage = renderDamageOrWeapon;
HANDLERS.weapon = renderDamageOrWeapon;

function renderSpatialActionContext(roll: Roll): string {
    let raw: unknown = roll.spatial_action_context_json;
    if (typeof raw === 'string' && raw !== '') {
        try { raw = JSON.parse(raw); } catch (_) { return ''; }
    }
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return '';

    const context = raw as Record<string, unknown>;
    if (context.policy === 'melee') {
        // Flanking (2026-08-26): attack-time C–T–A geometry, never a status. Only worth a badge when it fired.
        const rawFlank = context.flanking;
        const flank = rawFlank && typeof rawFlank === 'object' && !Array.isArray(rawFlank)
            ? rawFlank as Record<string, unknown>
            : null;
        if (flank?.flanked !== true) return '';
        const ally = typeof flank.allyName === 'string' && flank.allyName !== '' ? flank.allyName : '';
        const angle = Number(flank.angleDeg);
        const detail = [
            ally ? `with ${ally}` : '',
            Number.isFinite(angle) ? `${Math.round(angle)}°` : '',
        ].filter(Boolean).join(' · ');
        return `<span class="badge bg-success ms-1" title="An ally stands on the far side of the target — advantage on this melee attack">Flanking${detail ? ` · ${_esc(detail)}` : ''}</span>`;
    }
    if (context.policy !== 'ranged-projectile') return '';
    // No promoted FT3D shape → cover was never evaluated; that is not news on every ranged row either.
    if (context.geometryAuthoritative !== true) return '';
    const rawCover = context.cover;
    if (!rawCover || typeof rawCover !== 'object' || Array.isArray(rawCover)) return '';

    const cover = rawCover as Record<string, unknown>;
    const tier = String(cover.tier || 'none');
    const hunkered = cover.hunkerApplied === true;
    // Like flanking, cover only earns a badge when it changed the attack (owner 2026-09-10) —
    // "No cover · 0/9 rays" on every ranged row was pure log noise.
    if (tier === 'none' && !hunkered) return '';
    const labels: Record<string, string> = {
        none: 'No cover',
        partial: 'Partial cover',
        heavy: 'Heavy cover',
        blocked: 'Blocked',
    };
    const label = labels[tier];
    if (!label) return '';

    const delta = Number(cover.effectiveDefenseDelta || 0);
    const samples = Number(cover.blockedSamples || 0);
    const totalSamples = Number(cover.totalSamples || 0);
    const detail = [
        delta > 0 ? `+${delta} AC` : '',
        totalSamples > 0 ? `${samples}/${totalSamples} rays` : '',
        hunkered ? 'Hunkered' : '',
    ].filter(Boolean).join(' · ');
    const color = tier === 'blocked' ? 'bg-danger' : (tier === 'heavy' ? 'bg-warning text-dark' : (tier === 'partial' ? 'bg-info text-dark' : 'bg-secondary'));

    return `<span class="badge ${color} ms-1" title="Directional cover from the caster-to-target attack line">${_esc(label)}${detail ? ` · ${_esc(detail)}` : ''}</span>`;
}

function renderComplexRoll(roll: Roll) {
    const interpret = interpretRollResultArr(roll, Number(roll.campaign_permission || (window.FateTide?.user?.isGM ? 1 : 0)));
    return {
        showBigResult: true,
        body: `<div class="row m-0 pb-1 border-bottom">
            <div class="col-12 text-left">
                <span class="p-1">${_icon('complex_roll')}</span>
            </div>
        </div>
        ${interpret}`,
    };
}
HANDLERS.complex_roll = renderComplexRoll;

function damageOutcomeType(damageTypeId: string): { name: string; color: string } {
    const catalog = (typeof DAMAGETYPE_CATALOG !== 'undefined') ? DAMAGETYPE_CATALOG : null;
    const damageType = catalog?.[damageTypeId]
        ?? Object.values(catalog || {}).find(entry => String(entry?.item_id || '').toUpperCase() === damageTypeId.toUpperCase());
    const rawColor = String(damageType?.hex_color_code || '').trim();
    return {
        name: String(damageType?.item_name || '').trim(),
        color: /^#[0-9a-f]{3,8}$/i.test(rawColor) ? rawColor : '#f06b6b',
    };
}

function damageOutcomeBadge(amount: number, label: string, style: string, cls = 'text-dark'): string {
    return `<span class="badge ${cls}"${style} data-bs-toggle="tooltip"`
        + ` data-bs-placement="top" data-bs-original-title="${_esc(label)}" title="${_esc(label)}">${_esc(amount)}</span>`;
}

interface DamageOutcomeComponent { damageTypeId: string; applied: number | null; typed: number | null }

/**
 * Per-type split of a mixed-damage row, from the server's roll_feedback.damage_types (written by
 * calc_total_damage_and_apply_to_target). Empty unless the row carries two or more damage types.
 */
function damageOutcomeComponents(roll: Roll): DamageOutcomeComponent[] {
    let feedback: unknown = roll.roll_feedback;
    if (typeof feedback === 'string' && feedback !== '') {
        try { feedback = JSON.parse(feedback); } catch (_) { return []; }
    }
    const raw = feedback && typeof feedback === 'object' ? (feedback as Record<string, unknown>).damage_types : null;
    if (!Array.isArray(raw)) return [];
    const readAmount = (value: unknown): number | null => {
        if (value === null || value === undefined || value === '') return null;
        const n = Number(value);
        return Number.isFinite(n) && n >= 0 ? n : null;
    };
    const components: DamageOutcomeComponent[] = [];
    for (const entry of raw) {
        if (!entry || typeof entry !== 'object') continue;
        const record = entry as Record<string, unknown>;
        const damageTypeId = String(record.damage_type_id || '').trim();
        if (damageTypeId === '') continue;
        components.push({ damageTypeId, applied: readAmount(record.applied), typed: readAmount(record.typed) });
    }
    return components.length >= 2 ? components : [];
}

function damageOutcomeBlock(roll: Roll): string {
    const damageTypeId = String(roll.roll_type_2 || '');
    const rowType = damageOutcomeType(damageTypeId);
    const damageTypeName = rowType.name || String(roll.roll_type_2_name || 'Damage').trim();
    const color = rowType.color;
    const amount = Math.max(0, Number(roll.roll_result || 0));
    if (!Number.isFinite(amount)) return '';

    // Keep the value behind the same privacy predicate as interpretRollResultArr(). This sentence
    // is deliberately made from the canonical post-mitigation damage row, never inferred from the
    // target's before/after vitality, so armor, resistance and absorption stay truthful.
    const permission = Number(roll.campaign_permission || (window.FateTide?.user?.isGM ? 1 : 0));
    if (Number(roll.roll_private || 0) > 0 && permission === 0) return '';

    const itemId = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || 'The effect').trim();
    // UUID damage rows already render their ability/item badge immediately above this sentence.
    // Repeat a source only for legacy rows that carry no item id; otherwise the projected HUD reads
    // "Guiding Bolt Guiding Bolt dealt …" after flattening the card to one line.
    const source = itemId === '' ? `${_esc(itemName)} ` : '';
    const absorbed = amount <= 0 ? ' <span class="text-muted">(absorbed)</span>' : '';
    const open = `<div class="ft-log-damage-outcome text-left pt-1" data-ft-damage-outcome>${source}dealt `;

    // Mixed damage (Fire + Cold): one colour-coded badge per type instead of labelling the whole
    // total with the first fragment's type. When untyped DR / mitigation / percentages / transfer
    // then changed the total, the per-type split is pre-adjustment, so the true total follows.
    const components = damageOutcomeComponents(roll);
    const exact = components.length > 0 && components.every(c => c.applied !== null);
    const split = exact || (components.length > 0 && components.every(c => c.typed !== null));
    if (split) {
        const typeNames: string[] = [];
        const badges = components.map((c) => {
            const t = damageOutcomeType(c.damageTypeId);
            const name = t.name || 'Untyped';
            typeNames.push(name);
            const value = Math.max(0, Number(exact ? c.applied : c.typed));
            return damageOutcomeBadge(value, `${value} ${name} damage`, ` style="background-color:${_esc(t.color)}"`);
        }).join(' + ');
        const typedSum = components.reduce((sum, c) => sum + Math.max(0, Number(exact ? c.applied : c.typed)), 0);
        const total = exact && typedSum === amount
            ? ''
            : ` → ${damageOutcomeBadge(amount, `${amount} total damage after adjustments (${typeNames.join(' + ')})`, '', 'bg-secondary')}`;
        return `${open}${badges}${total}${absorbed}</div>`;
    }

    if (components.length > 0) {
        // Older mixed row with no per-type amounts: never attribute the whole total to one type.
        const names = components.map(c => damageOutcomeType(c.damageTypeId).name || 'Untyped').join(' + ');
        return `${open}${damageOutcomeBadge(amount, `${amount} damage (${names})`, '', 'bg-secondary')}${absorbed}</div>`;
    }

    const damageLabel = `${amount} ${damageTypeName} damage`;
    return `${open}${damageOutcomeBadge(amount, damageLabel, ` style="background-color:${_esc(color)}"`)}${absorbed}</div>`;
}

function renderUuidTypedRoll(roll: Roll) {
    const t = String(roll.roll_type_1 || '');

    const rollTypeCatalog = (typeof ROLLTYPE_CATALOG !== 'undefined') ? ROLLTYPE_CATALOG : null;
    const tempVId = String(rollTypeCatalog?.temporary_vitality_roll?.item_id || '');
    if (tempVId !== '' && t === tempVId && String(roll.roll_type_2 || '') === 'current_v_temp_not_exceeded') {
        return '';
    }

    const skillRollId = String(rollTypeCatalog?.skill_roll?.item_id || '');
    const dcRollId    = String(rollTypeCatalog?.dc_roll?.item_id || '');
    const damageRollId = String(rollTypeCatalog?.damage_roll?.item_id || '');
    const damageOverTimeRollId = String(rollTypeCatalog?.damage_over_time_roll?.item_id || '');

    const t2 = String(roll.roll_type_2 || '');
    const itemId = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || 'Unknown');
    const rollDc = Number(roll.roll_dc || 0);
    const skillCatalog = (typeof SKILL_CATALOG !== 'undefined') ? SKILL_CATALOG : null;
    const itemIdIsSkill = !!(skillCatalog && itemId !== '' && skillCatalog[itemId]);
    const t2IsSkill = !!(skillCatalog && t2 !== '' && skillCatalog[t2]);
    const t2PreferredAttr = t2IsSkill ? ' data-ft-log-preferred-icon="1"' : '';

    const t2IconBlock = t2 !== ''
        ? `<span${t2PreferredAttr} data-ft-log-action="open-item-info" data-ft-item-id="${_esc(t2)}" type="button">${_icon(t2)} </span>`
        : '';

    // Essence rolls carry the legacy roll_type_1='essence' marker; show the essence's icon
    // (iconMap of its roll_item_id) like skills do, instead of a name badge ("body"/"mind"/…).
    const itemIdIsEssence = (t === 'essence');

    let skillOrBadgeBlock = '';
    if (t !== skillRollId && (itemIdIsSkill || itemIdIsEssence)) {
        skillOrBadgeBlock = `<span data-ft-log-preferred-icon="1" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}" type="button">${_icon(itemId)}</span>`;
    } else if (t !== skillRollId && itemId !== '') {
        skillOrBadgeBlock = `<span class="badge bg-secondary" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}" type="button">${_esc(itemName)}</span>`;
    }

    const dcBlock = (t === dcRollId && dcRollId !== '' && rollDc > 0)
        ? `vs. <span class="badge bg-warning text-dark">${_esc(rollDc)}</span>`
        : '';
    const spatialBlock = renderSpatialActionContext(roll);

    // An encoded empty array ("[]") is common for modifier-only automatic
    // regeneration. Treat it as no dice breakdown: otherwise this header gets
    // a divider and targetThumb immediately adds another one, producing two
    // empty horizontal bars in the log card.
    let rollsFullValue: unknown = roll.rolls_full;
    if (typeof rollsFullValue === 'string' && rollsFullValue !== '') {
        try { rollsFullValue = JSON.parse(rollsFullValue); } catch (_) { /* legacy non-JSON value */ }
    }
    const hasRollsFull = Array.isArray(rollsFullValue)
        ? rollsFullValue.length > 0
        : rollsFullValue !== undefined && rollsFullValue !== null && rollsFullValue !== '';
    const headerBorder = hasRollsFull ? 'border-bottom pb-1' : '';

    const interpret = interpretRollResultArr(roll, Number(roll.campaign_permission || (window.FateTide?.user?.isGM ? 1 : 0)));
    const damageOutcome = (t !== '' && (t === damageRollId || t === damageOverTimeRollId))
        ? damageOutcomeBlock(roll)
        : '';
    const displacementCause = damageOutcome && Array.isArray(rollsFullValue)
        ? rollsFullValue.find(tuple => Array.isArray(tuple) && typeof tuple[1] === 'string'
            && tuple[1].startsWith('Materialised inside solid:'))?.[1]
        : '';

    const targetThumb = roll.roll_to
        ? `<div class="pb-1 mb-1 border-bottom"></div>
           <div class="d-flex align-items-center justify-content-center" data-ft-log-target="1">
               ${renderLogLineThumbnail(roll.roll_to_arr || {}, { thumbnail_link: 1 })}
           </div>`
        : '';

    return {
        showBigResult: true,
        body: `<div class="row m-0">
            <div class="col-12 text-left ${headerBorder}">
                <span data-ft-log-action="open-item-info" data-ft-item-id="${_esc(t)}" type="button">${_icon(t)} </span>
                ${t2IconBlock}
                ${skillOrBadgeBlock}
                ${dcBlock}
                ${spatialBlock}
            </div>
        </div>
        ${damageOutcome}
        ${displacementCause ? `<div class="text-left">${_esc(displacementCause)}</div>` : ''}
        ${damageOutcome ? `<div data-ft-damage-breakdown>${interpret}</div>` : interpret}
        ${targetThumb}`,
    };
}

(function _registerUuidRollHandlers() {
    const cat = (typeof ROLLTYPE_CATALOG !== 'undefined') ? ROLLTYPE_CATALOG : null;
    if (!cat) return;
    [
        'skill_roll', 'contesting_roll', 'dc_roll', 'damage_roll',
        'healing_roll', 'regeneration_roll',
        'temporary_vitality_roll', 'temporary_vitality_regeneration_roll',
        'temporary_max_vitality_roll',
        'damage_over_time_roll',
        'extra_damage_roll', 'extra_damage_roll_taken',
        'extra_weapon_damage_roll', 'extra_healing_roll',
        'recharge_ability_roll', 'armor_bending_roll',
        'summon_roll', 'mana_drain_roll',
        'increase_luck_roll', 'reduce_luck_roll',
        'increase_mana_roll', 'reduce_mana_roll',
        'reduce_damage_roll', 'essence_roll',
        'dying_stratum_roll',
        'spell_melee_attack_roll', 'spell_ranged_attack_roll',
        'weapon_melee_attack_roll', 'weapon_ranged_attack_roll',
        'crafting_roll', 'lockpicking_roll',
        'challenge_roll_assistance_roll', 'skill_roll_assistance_roll',
        'skill_roll_retracting_roll', 'weapon_attack_roll_assistance_roll',
    ].forEach(vn => {
        const id = cat[vn]?.item_id;
        if (id) HANDLERS[id] = renderUuidTypedRoll;
    });
})();

// Legacy alias: janus inserts essence rolls with roll_type_1='essence' (a plain string,
// NOT the essence_roll RollType uuid that skill/damage/etc. typed rolls store). The
// uuid-keyed registration above therefore never matched 'essence', so essence rolls hit
// `if (!handler) return ''` and rendered to nothing. Map the legacy string to the same
// renderer — renderUuidTypedRoll tolerates a non-uuid roll_type_1 (its uuid comparisons
// fall through) and renders the essence from roll_item_id. The PHP side keeps the 'essence'
// string (insert_roll roll_type_1 + audio_helpers depend on it).
HANDLERS['essence'] = renderUuidTypedRoll;

// Same legacy-string class as 'essence' above: skill_roll_arr callers stamp
// roll_type_1 with the *item_type* string of the skill used — the initiative
// system inserts 'innate_skill' rows (Initiative skill) — so the uuid-keyed
// registrations never match and the rows rendered to '' (blank entries inside
// the grouped-log dropdown after Roll Initiative).
[
    'skill', 'innate_skill', 'gathering_skill', 'knowledge_skill',
    'crafting_skill', 'instrument_skill', 'weapon_skill', 'armor_skill',
    'defensive_skill',
].forEach(t => { HANDLERS[t] = renderUuidTypedRoll; });

function renderTrainingRoll(roll: Roll) {
    const trCatalog = (typeof ROLLTYPE_CATALOG !== 'undefined' ? ROLLTYPE_CATALOG : null);
    const trTypeId = String(trCatalog?.training_roll?.item_id || '');
    const skillId = String(roll.roll_item_id || '');
    const skillName = String(roll.roll_item_id_item_name || 'skill');
    const dc = parseInt(String(roll.roll_dc || 0), 10);
    const mod = parseInt(String(roll.roll_mod || 0), 10);
    const total = parseInt(String(roll.roll_result || 0), 10);
    const ok = String(roll.roll_type_2 || '') === 'success';

    let natural: number | null = null;
    let rfArr: unknown = roll.rolls_full;
    if (typeof rfArr === 'string' && rfArr !== '') {
        try { rfArr = JSON.parse(rfArr); } catch (_) { rfArr = null; }
    }
    if (Array.isArray(rfArr)) {
        for (const tuple of rfArr) {
            if (Array.isArray(tuple) && tuple.length >= 2 && String(tuple[1]) === 'd20_1') {
                natural = parseInt(String(tuple[0]), 10);
                break;
            }
        }
    }
    if (natural === null) natural = total - mod;

    let d20Bg = 'bg-primary';
    if (natural === 20) d20Bg = 'bg-success';
    else if (natural === 1) d20Bg = 'bg-danger';

    const dcLine = dc > 0
        ? `<div class="text-left mb-2">
            <span class="fw-semibold">${_esc(total)}</span><span class="text-muted"> vs. </span><span
                class="badge bg-warning text-dark"
                data-bs-toggle="tooltip" data-bs-placement="top"
                data-bs-original-title="Fixed DC">DC ${_esc(dc)}</span>
        </div>`
        : '';

    const headerIcon = trTypeId
        ? `<span role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(trTypeId)}">${_icon(trTypeId)}</span>`
        : _icon('training_roll');

    const skillRow = skillId !== ''
        ? `<span role="button" data-ft-log-preferred-icon="1" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(skillId)}">${_icon(skillId)}</span>
           <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(skillId)}">${_esc(skillName)}</span>`
        : '';

    return {
        showBigResult: true,
        body: `<div>
            <div class="text-left d-flex align-items-center gap-1">
                ${headerIcon}
                <span class="fw-semibold">Training Roll</span>
            </div>
            <div class="text-left border-bottom pb-1 mb-1 mt-1"></div>
            <div class="text-left mb-1">
                <span role="button" class="badge ${d20Bg} d-inline-block"
                    data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="d20">${_esc(natural)}</span>
                <span class="text-muted fw-light mx-1">+</span>
                <span class="badge bg-warning d-inline-block"
                    data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="Mind">${_esc(mod)}</span>
                <span class="text-muted fw-light mx-1">=</span>
                <span class="fw-semibold">${_esc(total)}</span>
            </div>
            ${dcLine}
            <div class="text-left d-flex flex-wrap align-items-center gap-2">
                ${skillRow}
                <span class="${ok ? 'text-success' : 'text-warning'} fw-semibold">${ok ? 'Success' : 'Failure'}</span>
            </div>
        </div>`,
    };
}
HANDLERS.training_roll = renderTrainingRoll;

function renderGatheringRoll(roll: Roll) {
    const base = renderUuidTypedRoll(roll);
    if (typeof base === 'string') return '';
    const dc = parseInt(String(roll.roll_dc || 0), 10);
    const total = parseInt(String(roll.roll_result || 0), 10);
    const ok = (dc > 0) && (total >= dc);
    const dcLine = dc > 0
        ? `<div class="text-left mb-2">
            <span class="fw-semibold">${_esc(total)}</span><span class="text-muted"> vs. </span><span
                class="badge bg-warning text-dark"
                data-bs-toggle="tooltip" data-bs-placement="top"
                data-bs-original-title="Fixed DC">DC ${_esc(dc)}</span>
        </div>`
        : '';
    const okLine = `<div class="text-left">
        <span class="${ok ? 'text-success' : 'text-warning'} fw-semibold">${ok ? 'Success' : 'Failure'}</span>
    </div>`;
    return {
        showBigResult: true,
        body: `${base.body}<div>${dcLine}${okLine}</div>`,
    };
}
(function _registerGatheringRoll() {
    const cat = (typeof ROLLTYPE_CATALOG !== 'undefined') ? ROLLTYPE_CATALOG : null;
    const id = cat?.gathering_roll?.item_id;
    if (id) HANDLERS[id] = renderGatheringRoll;
    // Excavation (digsites) completes through the same skill-vs-DC shape — reuse the
    // gathering renderer so the completion roll shows its DC + Success/Failure line.
    const excavationId = cat?.excavation_roll?.item_id;
    if (excavationId) HANDLERS[excavationId] = renderGatheringRoll;
})();

const _CASCADE_TRIGGER_LABELS: Record<string, string> = {
    remarkable_crit_trigger_d20:    'Remarkable Crit Trigger',
    world_shaking_crit_trigger_d20: 'World Shaking Crit Trigger',
};
function renderCascadeTriggerD20(roll: Roll) {
    const t = roll.roll_type_1;
    const label = _CASCADE_TRIGGER_LABELS[t] || 'Crit Trigger';
    const rollDc = Number(roll.roll_dc || 0);
    const dcBlock = rollDc > 0
        ? `vs. <span class="badge bg-warning text-dark">${_esc(rollDc)}</span>`
        : '';
    const hasRollsFull = roll.rolls_full !== undefined && roll.rolls_full !== null && roll.rolls_full !== '';
    const headerBorder = hasRollsFull ? 'border-bottom pb-1' : '';
    const interpret = interpretRollResultArr(roll, Number(roll.campaign_permission || (window.FateTide?.user?.isGM ? 1 : 0)));
    return {
        showBigResult: true,
        body: `<div class="row m-0">
            <div class="col-12 text-left ${headerBorder}">
                <span class="badge bg-info text-dark">${_esc(label)}</span>
                ${dcBlock}
            </div>
        </div>
        ${interpret}`,
    };
}
HANDLERS.remarkable_crit_trigger_d20    = renderCascadeTriggerD20;
HANDLERS.world_shaking_crit_trigger_d20 = renderCascadeTriggerD20;

HANDLERS.random_table           = renderRandomTable;
HANDLERS.random_loot_table      = renderRandomTable;
HANDLERS.random_name_table      = renderRandomTable;
HANDLERS.random_encounter_table = renderRandomTable;

HANDLERS.creature_transform       = renderDeathAnchorOptimistic;
HANDLERS.start_training           = renderStartTraining;
HANDLERS.training_skill_tier_up   = renderTrainingSkillTierUp;
HANDLERS.ascension_skill_tier_up  = renderTrainingSkillTierUp;
HANDLERS.gained_ascension_skill_point          = renderAscensionSkillPointRoll;
HANDLERS.failed_roll_for_ascension_skill_point = renderAscensionSkillPointRoll;
HANDLERS.finish_reading           = renderFinishReading;
HANDLERS.transfer_damage_percentage_to_caster_of_status_effect = renderTransferDamage;

// Phase 7 — high-frequency remaining types

function renderAdjustCharges(roll: Roll) {
    const itemId = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    const mod = Number(roll.roll_mod || 0);
    const iconKey = mod >= 0 ? 'increase_charge' : 'decrease_charge';
    return {
        showBigResult: true,
        body: `<div>
            <div class="text-left border-bottom pb-1 mb-1">
                ${_icon(iconKey)} Adjust Charges
            </div>
            <div class="text-left">
                <span class="badge bg-danger" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_esc(itemName)}</span>
            </div>
        </div>`,
    };
}
HANDLERS.adjust_charges = renderAdjustCharges;

function renderChangeCharges(roll: Roll) {
    const itemId = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    return {
        showBigResult: true,
        body: `<div>
            <div class="text-left border-bottom pb-1 mb-1">
                ${_icon('change_charges')} Change Charges
            </div>
            <div class="text-left">
                <span class="badge bg-danger" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_esc(itemName)}</span>
            </div>
        </div>`,
    };
}
HANDLERS.change_charges = renderChangeCharges;

HANDLERS.resource_reduction_prevented = (roll: Roll) => {
    const resource = roll.roll_type_2 === 'mana' ? 'mana' : 'charges';
    return { showBigResult: false, body: `<div>${_icon(resource === 'mana' ? 'mana' : 'ability_charges')} No ${resource} spent: ${resource} cannot be reduced.</div>` };
};

function renderSpeedTypeIncreased(roll: Roll) {
    const itemId = String(roll.roll_item_id || '');
    return {
        showBigResult: true,
        body: `<div class="row m-0">
            <div class="col-12 text-left">${_icon(itemId)} Increased</div>
        </div>`,
    };
}
HANDLERS.speed_type_increased = renderSpeedTypeIncreased;

function renderSpeedTypeMoved(roll: Roll) {
    const t2 = String(roll.roll_type_2 || '');
    return {
        showBigResult: true,
        body: `<div class="col-12 text-left">${_icon(t2)}</div>`,
    };
}
HANDLERS.speed_type_moved = renderSpeedTypeMoved;

function renderInventoryThrown(roll: Roll) {
    const itemId = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    return {
        showBigResult: true,
        body: `<div class="text-left">
            <div class="border-bottom pb-1 mb-1 col-12">
                <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_esc(itemName)}</span>
            </div>
            <div class="d-flex align-items-center justify-content-center col-12">
                ${renderLogLineThumbnail(roll.roll_item_arr || {}, { thumbnail_link: 1 })}
            </div>
        </div>`,
    };
}
HANDLERS.inventory_thrown = renderInventoryThrown;

function renderStatusEffectMoved(roll: Roll) {
    const itemId = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    return {
        showBigResult: true,
        body: `<div class="text-left">
            <div class="d-flex align-items-center justify-content-center col-12">
                <div class="p-2">${_icon(roll.roll_type_1)}</div>
                <div class="p-2">
                    <span class="badge bg-info" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}" type="button">${_esc(itemName)}</span>
                </div>
            </div>
        </div>`,
    };
}
HANDLERS.status_effect_moved = renderStatusEffectMoved;

function renderActivityBadge(roll: Roll) {
    const t = roll.roll_type_1;
    const itemId = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    return `<div class="text-left border-bottom pb-1 mb-1">
        ${_icon(t)} ${_esc(_ucwords(t))}
    </div>
    <div class="text-left">
        <span class="badge bg-secondary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}" type="button">${_esc(itemName)}</span>
    </div>`;
}
HANDLERS.crafting = renderActivityBadge;

// Gathering gets its own renderer: the generic badge only showed "Gathering / Gathering".
// The PHP gathering log now carries the gathering skill in use on roll_type_2 (Mining,
// Herbalism, …) and WHAT is being gathered on roll_item_id, so surface both.
function renderGathering(roll: Roll) {
    const skillId   = String(roll.roll_type_2 || '');
    const skillName = String(roll.roll_type_2_name || '').trim();
    const itemId    = String(roll.roll_item_id || '');
    const itemName  = String(roll.roll_item_id_item_name || '').trim();

    // Header = the actual gathering skill (Mining, Herbalism, …); fall back to the generic activity.
    const headerIcon  = skillId ? _icon(skillId) : _icon('gathering');
    const headerLabel = skillName || 'Gathering';
    const header = skillId
        ? `<span role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(skillId)}">${headerIcon} ${_esc(headerLabel)}</span>`
        : `${headerIcon} ${_esc(headerLabel)}`;

    // Body = the PICTURE of what is being gathered, centered at the normal log thumbnail size
    // (same d-table mx-auto pattern as disenchanting). Name kept as the thumbnail tooltip + click-through.
    const gatheredThumb = renderLogLineThumbnail(
        { pic_url: roll.roll_item_pic_url, item_id: itemId, item_name: itemName },
        { thumbnail_link: 1 },
    );

    return `<div class="text-left border-bottom pb-1 mb-1">
        ${header}
    </div>
    <div class="text-center text-muted mb-1">Gathering</div>
    <div class="pt-1 d-table mx-auto">
        ${gatheredThumb}
    </div>`;
}
HANDLERS.gathering = renderGathering;

function renderActivityWithTargetThumb(roll: Roll) {
    const t = roll.roll_type_1;
    const itemId = String(roll.roll_item_id || '');
    return `<div class="text-left border-bottom pb-1 mb-1">
        <span role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_icon(t)} ${_esc(_ucwords(t))}</span>
    </div>
    <div class="pt-1 d-table mx-auto" data-ft-log-target="1">
        ${renderLogLineThumbnail(roll.roll_to_arr || {}, { thumbnail_link: 1 })}
    </div>`;
}
HANDLERS.disenchanting = renderActivityWithTargetThumb;
HANDLERS.object_identified = renderActivityWithTargetThumb;

function renderUpdatedPreferredSpeedType(roll: Roll) {
    const itemId = String(roll.roll_item_id || '');
    return `<div class="text-left border-bottom pb-1 mb-1">${_esc(_ucwords(roll.roll_type_1))}</div>
        <div class="text-left col-12">${_icon(itemId)}</div>`;
}
HANDLERS.updated_preferred_speed_type = renderUpdatedPreferredSpeedType;

function renderSpellConcentration(roll: Roll) {
    const spellId = String(roll.roll_item_id || '');
    const spellName = String(roll.roll_item_id_item_name || '');
    const spellLink = spellName
        ? `<div class="text-left">
            <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(spellId)}">${_esc(spellName)}</span>
        </div>`
        : '';
    return `<div class="row m-0">
        <div class="col-12 text-left">
            ${_icon(spellId)} Is Concentrating
        </div>
    </div>
    ${spellLink}`;
}
HANDLERS.spell_concentration = renderSpellConcentration;

// Status-lifecycle icon (expired / paused / resumed). A status applied FROM an ability / spell / curse
// has NO registered PNG, so iconMap() would fall to the "?" help-circle. For those, probe the encounter
// caches (window.ftFindFullNestedItem — the same resolver openItemInfo renders cards from) for the
// status's FULL def and, when it carries rune-able layers, render the L1 rune sigil (size 72 → LOD 1,
// shown at 64px via .ft-rune-log) in its place — zero DB, zero new log payload. Falls straight back to
// the iconMap icon when the def isn't cached (historical rows, a creature no longer in cache) or the
// status has no layers to draw, so there's no regression. The fresh mount is hydrated by
// window.ftMountRuneIcons() at each row-insert site (prependLogRow / _ftAppendToExistingGroup /
// buildLog→loadPostFunctions). Mirrors creature-console.ts's icon-less-status rune fallback.
function statusLifecycleIconHtml(itemId: string, hasItemIcon: boolean, iconKey: string): string {
    if (!hasItemIcon && itemId) {
        // Fast paths only (root catalogs / a fetched root): a history row often names a status no cached root holds, and
        // the full walk then only missed — on every log render (PRES lane census 2026-09-27). Falls back to the old finder
        // on a page that predates the no-walk export.
        const finder = window.ftFindCachedFullItemNoWalk ?? window.ftFindFullNestedItem;
        const def = (typeof finder === 'function') ? finder(itemId) : null;
        if (def && statusRunes(itemId, def)) {
            return statusRuneMountHtml(def, { size: 72, className: 'ft-rune-log' });
        }
    }
    return _icon(iconKey);
}

function renderStatusEffectLifecycle(roll: Roll) {
    const itemId = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    const sourceType = String(roll.roll_item_id_item_type || '');
    const hasItemIcon = !!((typeof ICON_LOOKUP !== 'undefined') && itemId && Object.prototype.hasOwnProperty.call(ICON_LOOKUP, itemId));
    const iconKey = hasItemIcon ? itemId : (sourceType || itemId);
    const iconHtml = statusLifecycleIconHtml(itemId, hasItemIcon, iconKey);
    const title = _esc(_ucwords(roll.roll_type_1));
    const body = itemName
        ? `<div class="text-left col-12">
            <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${iconHtml}</span>
            <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_esc(itemName)}</span>
        </div>`
        : `<div class="text-left col-12">${iconHtml}</div>`;
    return `<div class="text-left col-12 border-bottom pb-1 mb-1">${title}</div>
        ${body}`;
}
HANDLERS.status_effect_expired = renderStatusEffectLifecycle;
HANDLERS.status_effect_paused  = renderStatusEffectLifecycle;
HANDLERS.status_effect_resumed = renderStatusEffectLifecycle;

function renderEffectEndsOnTargetEndOfTurn(roll: Roll) {
    const itemId     = String(roll.roll_item_id || '');
    const itemName   = String(roll.roll_item_id_item_name || '');
    const sourceType = String(roll.roll_item_id_item_type || '');
    const hasItemIcon = (typeof ICON_LOOKUP !== 'undefined') && itemId && Object.prototype.hasOwnProperty.call(ICON_LOOKUP, itemId);
    const iconKey = hasItemIcon ? itemId : (sourceType || itemId);
    const header = `<div class="text-left col-12 border-bottom pb-1 mb-1">
        <span role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_icon(iconKey)}</span>
        Ends on ${_icon('target')} END of Turn
    </div>`;
    const body = itemName
        ? `<div class="text-left col-12">
            <span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_esc(itemName)}</span>
        </div>`
        : '';
    return header + body;
}
HANDLERS.effect_ends_on_target_end_of_turn = renderEffectEndsOnTargetEndOfTurn;

function renderRawDiceRoll(roll: Roll) {
    const die = parseInt(String(roll.die || 0), 10);
    let rollsFull: unknown = roll.rolls_full;
    if (typeof rollsFull === 'string' && rollsFull !== '') {
        try { rollsFull = JSON.parse(rollsFull); } catch (_) { rollsFull = null; }
    }
    const rfLen = Array.isArray(rollsFull) ? rollsFull.length : 0;

    if (rfLen === 1 && die !== 100) {
        return { showBigResult: true, body: `${_icon('d' + die)}` };
    }
    if (rfLen === 1 && die === 100) {
        return {
            showBigResult: true,
            body: `<span class="badge border text-dark" style="background-color:#808080">d100</span>`,
        };
    }
    const interpret = interpretRollResultArr(roll, Number(roll.campaign_permission || (window.FateTide?.user?.isGM ? 1 : 0)));
    return {
        showBigResult: true,
        body: `<div class="row m-0 pb-1 border-bottom">
            <div class="col-12 text-left">
                <span class="p-1">${_icon('d' + die)}</span>
            </div>
        </div>
        ${interpret}`,
    };
}
HANDLERS.raw = renderRawDiceRoll;

function renderCreatureFell(roll: Roll) {
    return {
        showBigResult: true,
        body: `<div class="col-12 text-left d-flex align-items-center">${_icon('creature_fell')}<span class="ms-1">Falls</span></div>`,
    };
}
function renderCreatureSank(roll: Roll) {
    return {
        showBigResult: true,
        body: `<div class="col-12 text-left d-flex align-items-center">${_icon('creature_sank')}<span class="ms-1">Sinks</span></div>`,
    };
}
HANDLERS.creature_fell = renderCreatureFell;
HANDLERS.creature_sank = renderCreatureSank;

// Sanguine Bouncing: the dice-less "reused attack" row — the primary's attack roll measured
// against the SECONDARY target's EC (no second d20). Shows the shared total vs that EC + hit/miss.
function renderBouncingReusedAttack(roll: Roll) {
    const total = roll.roll_result ?? '';
    const dc = roll.roll_dc ?? '';
    const hit = total !== '' && dc !== '' && Number(total) >= Number(dc);
    return {
        showBigResult: true,
        body: `<div class="col-12 text-left d-flex align-items-center">${_icon('next_spell_bounce_cast')}`
            + `<span class="ms-1">Bounced attack (reused roll): ${total} vs EC ${dc} — ${hit ? 'Hit' : 'Miss'} ${renderSpatialActionContext(roll)}</span></div>`,
    };
}
HANDLERS.bouncing_spell_reused_attack = renderBouncingReusedAttack;
HANDLERS.sanguine_spell_modified = (roll: Roll) => ({
    showBigResult: false,
    body: `<div class="col-12 text-left">${_icon('sanguine')} ${_esc(roll.roll_type_2 || 'Sanguine')} applied to this spell.</div>`,
});

// Arcane Arrow (arcane_fletcher): the spell a scroll-bound arrow released reuses the ARROW's attack roll
// against the creature it struck — a dice-less row, same shape as the Bouncing one above.
function renderArcaneArrowReusedAttack(roll: Roll) {
    const total = roll.roll_result ?? '';
    const dc = roll.roll_dc ?? '';
    const hit = total !== '' && dc !== '' && Number(total) >= Number(dc);
    return {
        showBigResult: true,
        body: `<div class="col-12 text-left d-flex align-items-center">${_icon('arcane_fletcher')}`
            + `<span class="ms-1">Arcane Arrow (the arrow's roll): ${total} vs EC ${dc} — ${hit ? 'Hit' : 'Miss'} ${renderSpatialActionContext(roll)}</span></div>`,
    };
}
HANDLERS.arcane_arrow_reused_attack = renderArcaneArrowReusedAttack;

// Effect Reflection (effect_is_reflected_back): the target's reaction turned an incoming
// single-target attack-roll spell back on its own caster — whole effect, all or nothing (owner
// ruling 2026-08-15). Written by damage_roll's pre-roll reflect claim just before the retarget;
// the fresh attack roll against the caster's own EC follows as its own ordinary log rows.
// roll_by = the reflecting bearer, roll_to = the original caster, roll_item_id = the spell.
function renderEffectReflectedBack(roll: Roll) {
    const itemId   = String(roll.roll_item_id || '');
    const itemName = String(roll.roll_item_id_item_name || '');
    const nameHtml = (itemName && _isUuid(itemId))
        ? `<span class="link-primary" role="button" data-ft-log-action="open-item-info" data-ft-item-id="${_esc(itemId)}">${_esc(itemName)}</span>`
        : _esc(itemName || 'The spell');
    return `<div class="text-left col-12 border-bottom pb-2">${_icon('effect_is_reflected_back')} ${nameHtml}<span class="ms-1">is reflected back at its caster!</span></div>`
        + _roleToBlock(roll);
}
HANDLERS.effect_reflected_back = renderEffectReflectedBack;

// ===================================================================
// Public: renderSimpleLogRow
// ===================================================================

function renderSimpleLogRow(roll: Roll, opts?: RollHandlerOpts): string {
    // Marker rows the commit needs but the log never shows: a zone entry's once-only frame, and an area that reached nobody
    // (`area_no_targets` — owner 2026-10-14: the hits and the damage waterfall already say what an area did; nothing to add when it did nothing).
    if (!roll || roll.roll_type_1 === 'paoe_entry_once' || roll.roll_type_1 === 'area_no_targets') return '';

    const viewerHasCampaignPermission = Number(
        roll.campaign_permission || (window.FateTide?.user?.isGM ? 1 : 0),
    ) === 1;
    if (!viewerHasCampaignPermission) {
        const privacy = Number(roll.roll_private || 0);
        const casterIsLair = String(roll.roll_by_item_type || '').toLowerCase() === 'lair';
        const casterIsHidden = Number(roll.roll_by_encounter_creature_private || 0) === 1;

        // Defense in depth for callers that bypass renderLogWindowFromJson(): a secret roll or a
        // hidden non-lair caster must never produce markup. Hidden rolls intentionally do produce
        // one generic card, matching the retired server renderer without consulting concealed fields.
        if (privacy === 2 || (casterIsHidden && !casterIsLair)) return '';
        if (privacy === 1) {
            const body = `<div class="row m-0" data-ft-hidden-roll>`
                + `<div class="col-12 text-left"><span aria-label="Hidden roll">${_icon('hidden_roll')}</span>`
                + `<span class="ms-1">Roll</span></div></div>`;
            return _wrapRow(roll, body, opts);
        }
    }

    if (!roll.roll_type_1) return '';
    const handler = HANDLERS[roll.roll_type_1];
    if (!handler) return '';
    const result = handler(roll);
    if (!result) return '';
    let body = '';
    const handlerOpts: Record<string, unknown> = {};
    if (typeof result === 'string') {
        body = result;
    } else if (typeof result === 'object') {
        const robj = result as Record<string, unknown>;
        body = String(robj.body || '');
        for (const k in robj) if (k !== 'body') handlerOpts[k] = robj[k];
    }
    if (!body) return '';
    return _wrapRow(roll, body, Object.assign({}, handlerOpts, opts || {}));
}

// ===================================================================
// renderHouseRuleBanner
// ===================================================================

function renderHouseRuleBanner(roll: Roll) {
    if (!roll || !roll.adv_dis_trace_json) return '';
    let trace = roll.adv_dis_trace_json;
    if (typeof trace === 'string') {
        try { trace = JSON.parse(trace); } catch (_) { return ''; }
    }
    if (!Array.isArray(trace)) return '';

    let html = '';
    for (const t of trace) {
        if (!t || typeof t !== 'object') continue;
        if (t.log_banner !== 'campaign_house_rule') continue;
        const ruleId = t.origin_item_id || '';
        const isUuid = (typeof checkIfValidUUID === 'function')
            ? checkIfValidUUID(ruleId)
            : /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/.test(ruleId);
        const safeRule = _esc(ruleId);
        const badgeIcon = _icon('campaign_house_rule_type', 18, 18);
        if (isUuid) {
            html += `<div class="row m-0"><div class="col-12 text-left mb-1">
                <span role="button" class="badge bg-info align-middle campaign-house-rule-log-badge d-inline-flex align-items-center py-1 px-2" data-ft-log-action="open-item-info" data-ft-item-id="${safeRule}" title="View optional campaign rule">${badgeIcon}</span>
            </div></div>`;
        } else {
            html += `<div class="row m-0"><div class="col-12 text-left mb-1">
                <span class="badge bg-info align-middle d-inline-flex align-items-center py-1 px-2" title="Optional campaign rule">${badgeIcon}</span>
            </div></div>`;
        }
    }
    return html;
}

// ===================================================================
// Phase 4 — _ftAppendToExistingGroup + _FT_GROUP_CONTAINERS
// ===================================================================

const _FT_GROUP_CONTAINERS: GroupContainerDef[] = [
    {
        matchOuter: (li: Element) => li.classList.contains('mass-contest-rollup') ? li : null,
        innerSelector: ':scope > div.collapse.mass-contest-detail',
        labelSelector: '.mass-contest-toggle-label',
        labelFormat: (n: number, isHide: boolean) => `${isHide ? 'Hide' : 'Show'} details (${n})`,
        injectInline: true,
        injectWrapClass: '',
        // The initial render can emit a hidden 0-count toggle (summary shown, no rendered details
        // yet); reveal it when the first live detail arrives. Mirrors the death rollup.
        revealOnFirst: '.mass-contest-toggle-wrap',
    },
    {
        matchOuter: (li: Element) => {
            if (li.classList.contains('death-log-session-rollup')) return li;
            return li.querySelector(':scope .death-log-session-group');
        },
        innerSelector: ':scope > ul.collapse.death-log-detail, :scope > div.collapse.death-log-detail, :scope ul.collapse.death-log-detail, :scope div.collapse.death-log-detail',
        labelSelector: '.death-log-toggle-label',
        labelFormat: (n: number, isHide: boolean) => `${isHide ? 'Hide' : 'Show'} details (${n})`,
        injectInline: false,
        revealOnFirst: '.death-log-toggle-wrap',
    },
    {
        matchOuter: (li: Element) => {
            if (li.classList.contains('trade-log-trade-rollup')) return li;
            return li.querySelector(':scope .trade-log-trade-group');
        },
        innerSelector: ':scope > ul.collapse.trade-log-trade-detail, :scope > div.collapse.trade-log-trade-detail, :scope ul.collapse.trade-log-trade-detail, :scope div.collapse.trade-log-trade-detail',
        labelSelector: '.trade-log-trade-toggle-label',
        labelFormat: null,
        injectInline: false,
    },
    {
        matchOuter: (li: Element) => li.querySelector(':scope .ft-log-gate-failure-rollup'),
        innerSelector: ':scope > div.collapse.ft-log-gate-failure-collapse-body, :scope div.collapse.ft-log-gate-failure-collapse-body',
        labelSelector: '.ft-log-gate-toggle-label',
        labelFormat: (n: number, isHide: boolean) => `${isHide ? 'Hide ' : ''}gates / riders not met (${n})`,
        injectInline: false,
    },
    {
        matchOuter: (li: Element) => li.classList.contains('ft-roll-group-collapsed') ? li : null,
        innerSelector: ':scope > div.collapse.ft-roll-group-detail',
        labelSelector: '.ft-roll-group-toggle-label',
        labelFormat: (n: number, isHide: boolean) => `${isHide ? 'Hide' : 'Show'} details (${n} more)`,
        injectInline: true,
        injectWrapClass: '',
        // The initial render can emit a hidden 0-count toggle (anchor shown, all other members
        // rendered silent); reveal it when the first live detail arrives. Mirrors the death rollup.
        revealOnFirst: '.ft-roll-group-toggle-wrap',
    },
];

function _ftAppendToExistingGroup(rowLi: Element, msg: RollInsertMessage): boolean {
    if (!rowLi || !msg) return false;

    for (const def of _FT_GROUP_CONTAINERS) {
        const outer = def.matchOuter(rowLi);
        if (!outer) continue;
        const innerCollapse = outer.querySelector(def.innerSelector);
        if (!innerCollapse) continue;

        let newHtml: string = '';
        if (def.injectInline) {
            newHtml = renderSimpleLogRow(msg.roll, { inline: true }) || '';
            if (newHtml && def.injectWrapClass) {
                newHtml = `<div class="${def.injectWrapClass}">${newHtml}</div>`;
            }
        } else {
            newHtml = renderSimpleLogRow(msg.roll) || '';
        }
        if (!newHtml) return false;

        innerCollapse.insertAdjacentHTML('beforeend', newHtml);
        // Hydrate any rune mount the appended row delivered (an ability-sourced status's L1 sigil) —
        // the log is otherwise pure-static HTML, so a rune mount is the only push-path element needing
        // JS hydration. Scoped to the group; idempotent (skips already-mounted). Mirrors prependLogRow.
        if (typeof window.ftMountRuneIcons === 'function') {
            try { window.ftMountRuneIcons(innerCollapse); } catch (_) { /* rune bundle absent */ }
        }

        if (def.labelFormat) {
            const label = outer.querySelector(def.labelSelector);
            const button = (label ? label.closest('button[data-bs-toggle="collapse"]') : null) as HTMLButtonElement | null;
            if (label) {
                let prev: number;
                if (button && button.dataset.ftGroupCount !== undefined) {
                    prev = parseInt(button.dataset.ftGroupCount, 10) || 0;
                } else {
                    const countMatch = (label.textContent || '').match(/\((\d+)/);
                    prev = countMatch ? (parseInt(countMatch[1] || '0', 10) || 0) : 1;
                }
                const next = prev + 1;
                if (button) button.dataset.ftGroupCount = String(next);
                const isHide = /^Hide/i.test(label.textContent || '');
                label.textContent = def.labelFormat(next, isHide);

                if (def.revealOnFirst && prev === 0) {
                    const toggleWrap = outer.querySelector(def.revealOnFirst) as HTMLElement | null;
                    if (toggleWrap) toggleWrap.style.display = '';
                }
            }
        }
        return true;
    }

    return false;
}

// Document-level Bootstrap-collapse label delegate (show/hide → label flip).
const _FT_COLLAPSE_LABEL_CONFIG: CollapseLabelConfig[] = [
    { match: 'div.collapse.mass-contest-detail',     labelSel: '.mass-contest-toggle-label',     formatShow: (n: number) => `Show details (${n})`,                formatHide: (n: number) => `Hide details (${n})` },
    { match: 'div.collapse.death-log-detail',        labelSel: '.death-log-toggle-label',        formatShow: (n: number) => `Show details (${n})`,                formatHide: (n: number) => `Hide details (${n})` },
    { match: 'div.collapse.trade-log-trade-detail',  labelSel: '.trade-log-trade-toggle-label',  formatShow: () => 'Show details',                                formatHide: () => 'Hide details' },
    { match: 'div.collapse.ft-log-gate-failure-collapse-body', labelSel: '.ft-log-gate-toggle-label', formatShow: (n: number) => `Gates / riders not met (${n})`, formatHide: (n: number) => `Hide gates / riders not met (${n})` },
    { match: 'div.collapse.ft-roll-group-detail',    labelSel: '.ft-roll-group-toggle-label',    formatShow: (n: number) => `Show details (${n} more)`,           formatHide: (n: number) => `Hide details (${n} more)` },
];

function _ftFlipCollapseLabel(collapseEl: Element | null, isShowing: boolean): void {
    if (!collapseEl) return;
    for (const cfg of _FT_COLLAPSE_LABEL_CONFIG) {
        if (!collapseEl.matches(cfg.match)) continue;
        const wrap = collapseEl.closest('.mass-contest-rollup, .death-log-session-group, .trade-log-trade-group, .ft-log-gate-failure-rollup, .ft-roll-group-collapsed');
        const button = (wrap || document).querySelector('button[data-bs-toggle="collapse"]') as HTMLButtonElement | null;
        const label = button ? button.querySelector(cfg.labelSel) : null;
        if (!label) return;
        let n = 0;
        if (button && button.dataset.ftGroupCount !== undefined) {
            n = parseInt(button.dataset.ftGroupCount, 10) || 0;
        } else {
            const countMatch = (label.textContent || '').match(/\((\d+)/);
            n = countMatch ? parseInt(countMatch[1] || '0', 10) || 0 : 0;
        }
        label.textContent = isShowing ? cfg.formatHide(n) : cfg.formatShow(n);
        return;
    }
}

document.addEventListener('show.bs.collapse', (ev: Event) => _ftFlipCollapseLabel(ev.target as Element, true));
document.addEventListener('hide.bs.collapse', (ev: Event) => _ftFlipCollapseLabel(ev.target as Element, false));

// ===================================================================
// Exports
// ===================================================================

export {
    renderSimpleLogRow,
    renderLogLineThumbnail,
    renderLogTimestamp,
    renderHouseRuleBanner,
    _ftAppendToExistingGroup,
    normalizePicUrl,
};
